import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import PublicPayments from "components/publicpaymnts";
import InstallationPublicPayments from "components/InstallationPublicPayments";
import RemovalPublicPayments from "components/RemovalPublicPayments";
import {
  fetchPaymentGatewaysCreatedByUserId,
  fetchProjectApprovedMessage,
  resolveApprovedProofVersionSummary,
} from "components/proejctsandquoteapicalls";
import {
  computeInstallationPublicSummary,
  computeJobOrderTotals,
  computeRemovalPublicSummary,
  fetchNormalizedPublicOrderForSummary,
  fetchPublicPaymentMethodsCountBySupplierAndUser,
  finalizePublicOrderForSummary,
  getJobServiceShipNode,
  getSalesOrderChargesFirstRow,
  computeSalesShippingCharges,
  computeSalesSpecialFeeCharges,
  isPublicOrderCancelledForSummary,
  normalizeOrderUserId,
  pickLatestOrderPaymentInfo,
  displayPaymentPaidByName,
  resolveInstallationRemovalDiscountCode,
  resolveJobLineItemQuantity,
  resolveSignageIdFromOrder,
  sumOrderPaymentInfoAmountPaid,
} from "components/orderSummary";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import {
  buildInstallationOrderPdfAttachment,
  buildRemovalOrderPdfAttachment,
  buildSalesOrderPdfAttachment,
} from "utils/installationOrderPdfAttachment";
import { getAuthCookie, setSupplierIdContextFromValue } from "../utils/authCookies";
import {
  computeSalesSubtotalBeforeDiscountFromItems,
  getSalesOrderLineChangedUnitPrice,
  getSalesOrderLineDiscount,
  getSalesOrderLineTotalAmount,
  getSalesOrderLineUnitRetailPrice,
} from "components/invoiceDocument/invoiceDocumentSalesTotals";
import { getLatestAdditionalInstructionValue } from "components/invoiceDocument/invoiceDocumentJobNotes";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import { SALES_ORDER_CONFIRMED_STATUS_CODE } from "components/SalesOrderstatus";
import {
  sendInstallationOrderTriggerEmail,
  sendRemovalOrderTriggerEmail,
  sendSalesOrderTriggerEmail,
} from "utils/salesOrderTriggerEmailEngine";
import {
  formatShippingChargeOrderTotalsLabel,
  formatSpecialFeeOrderTotalsLabel,
} from "utils/orderTotalsChargeLabels";
import { deliveryAddressLabelForOrder } from "components/invoiceDocument/invoiceDocumentJobDisplay";
import type { InvoiceType } from "components/invoiceDocument/invoiceDocumentTypes";
import { parseCustomInformation } from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";
import { DiscountAmountDisplay } from "components/DiscountLabelBadge";

export type SummaryItem = {
  id?: string | number;
  imageUrl?: string;
  pdfFileId?: string;
  imageAlt?: string;
  productName?: string;
  unitRetailPrice?: number;
  changedUnitPrice?: number;
  sku?: string;
  details?: Record<string, string | number | null | undefined>;
  /** Product template customizations (text + file); file URLs resolved via `customizationImgMap` in the summary UI. */
  customizations?: { fields?: any[] } | null;
  inventorySource?: string;
  customInformation?: string;
  quantity?: number;
  lineDiscount?: number;
  tax?: number;
  total?: number;
};

export type SummaryAddress = {
  name?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

export type Status100OrderSummaryProps = {
  companyLogo?: string;
  companyName?: string;
  quoteNumber?: string;
  quoteDate?: string;
  paymentStatusLabel?: string;

  brokerage?: string;
  office?: string;
  agent?: string;
  contact?: string;
  email?: string;
  phone?: string;

  billingAddress?: SummaryAddress;
  shippingAddress?: SummaryAddress;

  items?: SummaryItem[];
  orderNotes?: string;

  subtotal?: number;
  shipping?: number;
  specialFees?: number;
  tax?: number;
  grandTotal?: number;

  payButtonLabel?: string;
  payUrl?: string;
  showPayButton?: boolean;

  footerPhone?: string;
  footerEmail?: string;
  footerAddress?: string;
  footerWebsite?: string;
  termsText?: string;
  poweredByText?: string;
  /** Gray text before the brand link (e.g. `"Powered by "`). */
  poweredByPrefix?: string;
  /** Blue linked brand name (e.g. `"RealEsy"`). */
  poweredByBrandText?: string;
  /** Opens in a new tab; default RealEsy marketing site. */
  poweredByUrl?: string;

  /** Off-screen email PDF: bind to `emailPdfOrder`, hide Pay, skip public fetch. */
  emailPdfMode?: boolean;
  emailPdfOrder?: any;
  emailPdfKind?: "sales" | "installation" | "removal";
  /** Bearer for line-item images during PDF rasterization (defaults to session cookie). */
  emailPdfImageBearerToken?: string;
  /**
   * When set with `emailPdfMode`, PDF shows "Already Paid" / "Remaining Balance" under Grand Total
   * (payment-applied email attachments only).
   */
  emailPdfPaymentBalance?: { alreadyPaid: number; remainingBalance: number } | null;
};

type InvoiceTemplate = {
  company_logo?: { url?: string };
  images?: { url?: string };
  company_name?: string;
  address?: string;
  phone_number?: string;
  email?: string;
  website?: string;
  custom_message?: string;
  job_types?: string[];
  default?: boolean;
  default_for?: string[];
};

const money = (value?: number) => `$${Number(value || 0).toFixed(2)}`;
const show = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "—" : String(value);
const formatSkuDisplay = (sku?: string | number | null) => {
  const raw = String(sku ?? "").trim();
  if (!raw) return "—";
  if (/^custom_sku(?:_|$)/i.test(raw)) return "Custom SKU";
  return raw;
};
const dateOnly = (value?: string | number | Date | null) => {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};
const dateTimeLabel = (value?: string | number | Date | null) => {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/** YYYY-MM-DD for job scheduled fields (matches work-order date display). */
const formatJobScheduledDateYmd = (raw: unknown): string => {
  const s = raw == null ? "" : String(raw).trim();
  if (!s) return "—";
  if (s.includes("T")) {
    const head = s.split("T")[0];
    return head || "—";
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const t = new Date(s).getTime();
  if (!Number.isNaN(t)) return new Date(t).toISOString().split("T")[0];
  return s;
};

const formatJobPriorityLabel = (priority: unknown, isRemoval: boolean): string => {
  if (priority === null || priority === undefined) return "—";
  const n = Number(priority);
  if (Number.isFinite(n)) {
    if (isRemoval) {
      if (n === 1) return "Critical";
      if (n === 2) return "High Priority";
      if (n === 3) return "Normal";
    } else {
      if (n === 1) return "High";
      if (n === 2) return "Medium";
      if (n === 3) return "Low";
    }
  }
  const str = String(priority).trim();
  if (!str) return "—";
  const lower = str.toLowerCase();
  if (["high", "medium", "low", "critical", "normal"].includes(lower)) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  return str;
};

const cleanParam = (value?: string | null) =>
  String(value || "")
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/^'+|'+$/g, "");

const looksLikeHttpUrl = (s: string) => /^https?:\/\//i.test(String(s).trim());
const toAbsoluteAssetUrl = (raw?: unknown): string => {
  const v = String(raw ?? "").trim();
  if (!v) return "";
  if (looksLikeHttpUrl(v)) return v;
  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const path = v.replace(/^\/+/, "").replace(/\\/g, "/");
  return api ? `${api}/${path}` : "";
};
const isLikelyImageUrl = (v?: unknown) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(String(v ?? "").trim());
const is24HexId = (value?: unknown) => /^[0-9a-f]{24}$/i.test(String(value || "").trim());
const extract24HexId = (value?: unknown): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (is24HexId(raw)) return raw;
  const m = raw.match(/[0-9a-f]{24}/i);
  return m ? m[0] : "";
};
const buildWorkDriveDownloadUrl = (fileId: string): string => {
  const id = String(fileId || "").trim();
  if (!is24HexId(id)) return "";
  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (!api) return "";
  return `${api}/public/work-drive/files/${id}/download`;
};

const attachmentMapToBlob = (map?: Record<string, string>): { name: string; blob: Blob } | null => {
  if (!map) return null;
  const entries = Object.entries(map);
  if (!entries.length) return null;
  const [name, base64] = entries[0];
  if (!base64) return null;
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return { name, blob: new Blob([arr], { type: "application/pdf" }) };
};

async function fetchOrderSummaryTemplates(signageId: string, token?: string): Promise<InvoiceTemplate[]> {
  const sid = String(signageId || "").trim();
  if (!sid) return [];
  const res = await fetch(`${process.env.REACT_APP_API_URL}/invoice-templates/signage/${encodeURIComponent(sid)}/default`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`Failed to load invoice templates (${res.status})`);
  const payload = await res.json();
  if (Array.isArray(payload)) return payload;
  if (payload?.data && !Array.isArray(payload.data)) return [payload.data];
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload && typeof payload === "object") return [payload];
  return [];
}

/** Resolve line-item image in the same way as SalesOrderDetails. */
const resolveLineItemImageUrl = (it: any, variantImgMap: Record<string, string>): string | undefined => {
  const hasDesignId = Boolean(String(it?.design_id || it?.studio_design_id || "").trim());
  const preview = it?.preview_url != null ? String(it.preview_url).trim() : "";
  if (hasDesignId && preview) {
    const previewResolved =
      getImageSrc(preview, variantImgMap) || variantImgMap[preview] || (looksLikeHttpUrl(preview) ? preview : undefined);
    if (previewResolved) return previewResolved;
  }

  const raw = it?.images?.[0]?.url != null ? String(it.images[0].url).trim() : "";
  const rawResolved = raw
    ? getImageSrc(raw, variantImgMap) || variantImgMap[raw] || (looksLikeHttpUrl(raw) ? raw : undefined)
    : undefined;
  if (rawResolved) return rawResolved;

  const keyByVariant = String(it?.variant_id ?? "");
  const keyByProduct = String(it?.product_id ?? "");
  const fromMapByVariantOrProduct = variantImgMap[keyByVariant] || variantImgMap[keyByProduct];
  if (fromMapByVariantOrProduct) return fromMapByVariantOrProduct;

  const imageUrlRaw = it?.image_url != null ? String(it.image_url).trim() : "";
  if (imageUrlRaw) {
    const imageUrlResolved = getImageSrc(imageUrlRaw, variantImgMap);
    if (imageUrlResolved) return imageUrlResolved;
    if (looksLikeHttpUrl(imageUrlRaw)) return imageUrlRaw;
  }

  return undefined;
};

const formatAddressLines = (address?: SummaryAddress) => {
  if (!address) return ["—"];

  const lines: string[] = [];

  // if (address.name) lines.push(address.name);
  // if (address.phone) lines.push(address.phone);
  if (address.address1) lines.push(address.address1);
  if (address.address2) lines.push(address.address2);

  const cityStateZip = [address.city, address.state, address.zip].filter(Boolean).join(", ");
  if (cityStateZip) lines.push(cityStateZip);

  if (address.country) lines.push(address.country);

  return lines.length ? lines : ["—"];
};

const productDetailsToLines = (details?: Record<string, string | number | null | undefined>) => {
  if (!details) return [];
  return Object.entries(details)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1),
      value: String(value),
    }));
};

const customizationIsFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
const customizationFirstFileVal = (raw: any): string => {
  if (!raw) return "";
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
  return "";
};
const customizationFieldHasValue = (f: any): boolean => {
  if (customizationIsFile(f)) return !!customizationFirstFileVal(f?.value);
  return String(f?.value ?? "").trim().length > 0;
};

const OrderSummaryLineItemAttrsBlock: React.FC<{
  details?: Record<string, string | number | null | undefined>;
}> = ({ details }) => {
  const attrLines = productDetailsToLines(details);
  if (!attrLines.length) {
    return <div className="status100-product-detail">—</div>;
  }
  return (
    <>
      {attrLines.map((detail, detailIndex) => (
        <div className="status100-product-detail" key={`attr-${detail.label}-${detailIndex}`}>
          {detail.label}: {detail.value}
        </div>
      ))}
    </>
  );
};

const OrderSummaryLineItemCustomizationsBlock: React.FC<{
  customizations?: { fields?: any[] } | null;
  customizationImgMap: Record<string, string>;
}> = ({ customizations, customizationImgMap }) => {
  const fields = Array.isArray(customizations?.fields) ? customizations.fields : [];
  const textFields = fields.filter((f: any) => !customizationIsFile(f) && customizationFieldHasValue(f));
  const fileFields = fields.filter((f: any) => customizationIsFile(f) && customizationFieldHasValue(f));
  const hasCustom = textFields.length > 0 || fileFields.length > 0;
  if (!hasCustom) return null;

  return (
    <>
      {textFields.map((field: any, idx: number) => (
        <div className="status100-product-detail" key={`cf-t-${field?._id || field?.field_name || idx}`}>
          {String(field?.field_name || "Field")}: {String(field?.value ?? "").trim()}
        </div>
      ))}
      {fileFields.map((field: any, idx: number) => {
        const id = customizationFirstFileVal(field?.value);
        const href =
          customizationImgMap[id] ||
          (getImageSrc(id, customizationImgMap) as string | undefined) ||
          (looksLikeHttpUrl(id) ? id : "");
        const label = String(field?.field_name || "Image").trim();
        return (
          <div className="status100-product-detail" key={`cf-f-${field?._id || label}-${idx}`}>
            <div className="mb-1">{label}</div>
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                <img src={href} alt={label} style={{ maxWidth: 100, height: "auto", borderRadius: 4 }} />
              </a>
            ) : /^[0-9a-f]{24}$/i.test(id) ? (
              <span style={{ color: "#6b7280", fontSize: 12 }}>Loading…</span>
            ) : null}
          </div>
        );
      })}
    </>
  );
};

const OrderSummaryLineItemCustomInformationBlock: React.FC<{
  inventorySource?: string;
  customInformation?: string;
}> = ({ inventorySource, customInformation }) => {
  if (String(inventorySource || "").toLowerCase() !== "custom") return null;

  const raw = String(customInformation ?? "").trim();
  if (!raw) return null;

  const pairs = parseCustomInformation(raw);
  const rows = pairs.length > 0 ? pairs : [{ key: "Details", value: raw }];

  return (
    <>
      {rows.map(({ key, value }, idx) => (
        <div className="status100-product-detail" key={`ci-${key || idx}`}>
          {key || "Field"}: {value}
        </div>
      ))}
    </>
  );
};

const OrderSummaryLineItemThumb: React.FC<{
  imageUrl?: string;
  pdfFileId?: string;
  imageAlt?: string;
  productName?: string;
}> = ({ imageUrl, pdfFileId, imageAlt, productName }) => {
  const safePdfFileId = String(pdfFileId || "").trim();
  const src = String(imageUrl || "").trim();
  const productImageSrc = src || imagecustomRounded;
  return (
    <div
      style={{
        width: 80,
        height: 80,
        minWidth: 80,
        minHeight: 80,
        borderRadius: 8,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <img
        className="status100-thumb"
        src={productImageSrc}
        data-pdf-file-id={safePdfFileId || undefined}
        alt={imageAlt || productName || "Product"}
        loading="eager"
        decoding="sync"
        style={{ width: 80, height: 80, objectFit: "contain", display: "block" }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
        }}
      />
    </div>
  );
};

const sectionIconBox: React.CSSProperties = {
  width: 26,
  height: 26,
  minWidth: 26,
  borderRadius: 6,
  border: "1px solid #d8dde6",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.975rem",
  color: "#111827",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 700,

};

const fieldLabel: React.CSSProperties = {
  marginBottom: 6,

  fontSize: "0.875rem",
  color: "#111827",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 500,
};

const fieldValue: React.CSSProperties = {
  lineHeight: 1.5,

  fontSize: "0.875rem",
  color: "#495363",
  letterSpacing: "0.01em",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 500,
  minWidth: 0,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const OrderSummaryStatus100Template = forwardRef<unknown, Status100OrderSummaryProps>(
  function OrderSummaryStatus100Template(
    {
      companyLogo,
      companyName = "Farmer Signs",
      quoteNumber,
      quoteDate,
      paymentStatusLabel = "Awaiting Payment",
      brokerage,
      office,
      agent,
      contact,
      email,
      phone,
      billingAddress,
      shippingAddress,
      items = [],
      orderNotes = "No notes on this order.",
      subtotal = 0,
      shipping = 0,
      specialFees = 0,
      tax = 0,
      grandTotal = 0,
      payButtonLabel,
      payUrl,
      showPayButton = true,
      footerPhone,
      footerEmail,
      footerAddress,
      footerWebsite,
      termsText = "",
      poweredByText = "Powered by RealEsy",
      poweredByPrefix = "Powered by ",
      poweredByBrandText = "RealEsy",
      poweredByUrl = "https://realesy.com/",
      emailPdfMode = false,
      emailPdfOrder,
      emailPdfKind = "sales",
      emailPdfImageBearerToken,
    },
    ref
  ) {
    const location = useLocation();
    const navigate = useNavigate();
    /** Merge React Router state with sessionStorage (SignageInstallationWorkOrder & others set both). */
    const effectivePreviewState = useMemo(() => {
      // console.log("location.state", location.state);
      const st = location.state as
        | { privateOrderSummaryPreview?: boolean; order?: any; fromSendQuote?: boolean }
        | null
        | undefined;
      // console.log("st", st);
      if (st?.privateOrderSummaryPreview && st?.order) return st;
      if (typeof window === "undefined") return null;
      try {
        const raw = sessionStorage.getItem("orderSummaryPrivatePreview");
        if (!raw) return null;
        // console.log("raw", raw);
        const parsed = JSON.parse(raw) as {
          privateOrderSummaryPreview?: boolean;
          order?: any;
          fromSendQuote?: boolean;
        };
        if (parsed?.privateOrderSummaryPreview && parsed?.order) return parsed;
      } catch {
        return null;
      }
      return null;
    }, [location.state, location.key]);

    const isPrivateOrderSummaryPreview = Boolean(
      effectivePreviewState?.privateOrderSummaryPreview && effectivePreviewState?.order
    );
    const [showPayModal, setShowPayModal] = useState(false);
    const [hasApprovedMessage, setHasApprovedMessage] = useState(false);
    const [approvedMessageData, setApprovedMessageData] = useState<any>(null);
    const [projectDetailMeta, setProjectDetailMeta] = useState<{
      approvedBy: string;
      approvedAt: string;
      assignedTo: string;
      shippingName: string;
      shippingDesc: string;
      shippingAmount: number | null;
    }>({
      approvedBy: "",
      approvedAt: "",
      assignedTo: "",
      shippingName: "",
      shippingDesc: "",
      shippingAmount: null,
    });
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    /** Dollar amount shown in the success dialog (this charge), not the full order total. */
    const [paymentSuccessChargedAmount, setPaymentSuccessChargedAmount] = useState<number | null>(null);
    const [clientSecret, setClientSecret] = useState("");
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [loadingPaymentUi, setLoadingPaymentUi] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
    const [userHasPaymentMethods, setUserHasPaymentMethods] = useState(false);
    const [loadingPaymentMethodsCount, setLoadingPaymentMethodsCount] = useState(false);
    const [finalizingOrder, setFinalizingOrder] = useState(false);
    const [approveError, setApproveError] = useState<string | null>(null);
    const [publicOrder, setPublicOrder] = useState<any | null>(() => {
      return isPrivateOrderSummaryPreview && effectivePreviewState?.order ? effectivePreviewState.order : null;
    });
    const displayOrder = effectivePreviewState?.order ?? publicOrder;
    const autoStripeRetryRef = useRef(false);
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryProjectId = cleanParam(query.get("project_id"));
    const queryAccessToken = cleanParam(query.get("access_token") || query.get("token"));
    const queryPublicUrl = cleanParam(query.get("public_url") || query.get("Public_url"));
    const queryOrderId = cleanParam(query.get("order_id")) || queryProjectId;
    const querySupplierId = cleanParam(query.get("supplier_id")) || queryProjectId;
    /** Gateway + payment-intent routes use the signage supplier user id, not the project id. */
    const stripeGatewayUserId = useMemo(() => {
      const fromQuery = cleanParam(query.get("supplier_id"));
      if (fromQuery) return fromQuery;
      const fromOrder = String(
        (publicOrder as any)?.signage_id ||
        (publicOrder as any)?.seller_owner_node_id ||
        ((publicOrder as any)?.created_by && typeof (publicOrder as any).created_by === "object"
          ? (publicOrder as any).created_by?.id
          : "") ||
        ""
      ).trim();
      if (fromOrder) return fromOrder;
      return queryProjectId;
    }, [query, publicOrder, queryProjectId]);

    /** Signage supplier Mongo id for payment emails / cookies — never use bare `project_id` as supplier. */
    const supplierUserIdForPaymentBranding = useMemo(() => {
      const fromQuery = cleanParam(query.get("supplier_id"));
      if (fromQuery) return fromQuery;
      const po = publicOrder as Record<string, unknown> | null | undefined;
      const sid = String(po?.signage_id || po?.seller_owner_node_id || "").trim();
      if (sid) return sid;
      const cb = po?.created_by as { id?: unknown } | undefined;
      if (cb && typeof cb === "object" && cb.id != null) {
        const id = String(cb.id).trim();
        if (id) return id;
      }
      return "";
    }, [query, publicOrder]);

    useEffect(() => {
      const id = String(supplierUserIdForPaymentBranding || "").trim();
      if (!id) return;
      setSupplierIdContextFromValue(id);
    }, [supplierUserIdForPaymentBranding]);
    // console.log("supplierUserIdForPaymentBranding", supplierUserIdForPaymentBranding);

    const queryClientSecret = useMemo(() => {
      const raw =
        query.get("client_secret") ??
        query.get("clientSecret") ??
        query.get("payment_intent_client_secret") ??
        "";
      return cleanParam(raw);
    }, [query]);
    const queryPublishableKey = useMemo(() => {
      const raw = query.get("publishable_key") ?? query.get("stripe_publishable_key") ?? "";
      return cleanParam(raw);
    }, [query]);
    const parsedPublicProjectId = useMemo(() => {
      if (!queryPublicUrl) return "";
      try {
        const parsed = queryPublicUrl.startsWith("http")
          ? new URL(queryPublicUrl)
          : new URL(queryPublicUrl, window.location.origin);
        const m = parsed.pathname.match(/\/public\/projects\/([^/?#]+)/i);
        return cleanParam(m?.[1] || "");
      } catch {
        return "";
      }
    }, [queryPublicUrl]);
    const parsedPublicToken = useMemo(() => {
      if (!queryPublicUrl) return "";
      try {
        const parsed = queryPublicUrl.startsWith("http")
          ? new URL(queryPublicUrl)
          : new URL(queryPublicUrl, window.location.origin);
        return cleanParam(parsed.searchParams.get("access_token") || parsed.searchParams.get("token"));
      } catch {
        return "";
      }
    }, [queryPublicUrl]);
    const effectiveProjectId = queryProjectId || parsedPublicProjectId;
    const effectiveAccessToken = queryAccessToken || parsedPublicToken;
    const paymentTriggerToken = String(effectiveAccessToken || getAuthCookie("sessionToken") || "").trim();
    const isInstallationPublicSummary = useMemo(
      () =>
        computeInstallationPublicSummary(
          location.pathname,
          isPrivateOrderSummaryPreview,
          effectivePreviewState?.order
        ),
      [location.pathname, isPrivateOrderSummaryPreview, effectivePreviewState?.order]
    );

    const isRemovalPublicSummary = useMemo(
      () =>
        computeRemovalPublicSummary(
          location.pathname,
          isPrivateOrderSummaryPreview,
          effectivePreviewState?.order
        ),
      [location.pathname, isPrivateOrderSummaryPreview, effectivePreviewState?.order]
    );

    const isJobOrderSummary = isInstallationPublicSummary || isRemovalPublicSummary;
    const io: any = publicOrder;
    const imageToken =
      getAuthCookie("sessionToken") ||
      effectiveAccessToken ||
      "";
    const presignImageToken = imageToken;
    const [templates, setTemplates] = useState<InvoiceTemplate[] | null>(null);

    const { lineItemsForImageMap, customizationIds } = useMemo(() => {
      const sourceItems =
        isJobOrderSummary && Array.isArray(io?.line_items)
          ? io.line_items
          : Array.isArray(publicOrder?.items)
            ? publicOrder.items
            : [];

      const customizationIdList: string[] = [];
      for (const it of sourceItems) {
        const cfs = Array.isArray(it?.customizations?.fields) ? it.customizations.fields : [];
        for (const f of cfs) {
          if (String(f?.field_type || "").toLowerCase() !== "file") continue;
          const raw = f?.value;
          const id =
            typeof raw === "string"
              ? raw.trim()
              : raw && typeof raw === "object" && raw.url
                ? String(raw.url).trim()
                : "";
          if (/^[0-9a-f]{24}$/i.test(id)) customizationIdList.push(id);
        }
      }

      const lineItemsForImageMap = sourceItems.map((item: any) => {
        const images = Array.isArray(item?.images) ? [...item.images] : [];
        const previewId = String(item?.preview_url || "").trim();
        const imageId = String(item?.image_url || "").trim();
        if (previewId) images.push({ url: previewId });
        if (imageId) images.push({ url: imageId });
        return { ...item, images };
      });

      return {
        lineItemsForImageMap,
        customizationIds: Array.from(new Set(customizationIdList)),
      };
    }, [isJobOrderSummary, io?.line_items, publicOrder?.items]);

    const customizationSyntheticRows = useMemo(
      () => (customizationIds.length ? [{ images: customizationIds.map((id) => ({ url: id })) }] : []),
      [customizationIds]
    );
    const customizationImgMap = useVariantImageUrlMap(customizationSyntheticRows, presignImageToken) ?? {};
    const variantImgMap = useVariantImageUrlMap(lineItemsForImageMap, presignImageToken);
    const signageIdForTemplate = useMemo(() => {
      const previewOrder = effectivePreviewState?.order ?? publicOrder;
      const fromOrder = resolveSignageIdFromOrder(previewOrder);
      if (fromOrder) return fromOrder;
      return (
        cleanParam(query.get("supplier_id")) ||
        stripeGatewayUserId ||
        ""
      ).trim();
    }, [query, effectivePreviewState?.order, publicOrder, stripeGatewayUserId]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          if (!signageIdForTemplate) { if (!cancelled) setTemplates([]); return; }
          const data = await fetchOrderSummaryTemplates(signageIdForTemplate, imageToken);
          if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
        } catch {
          if (!cancelled) setTemplates([]);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [imageToken, signageIdForTemplate]);

    const effOrder = effectivePreviewState?.order ?? publicOrder;
    const displayPONo = useMemo(() => {
      const raw = effOrder?.po_number ?? effOrder?.poNumber;
      if (raw == null || String(raw).trim() === "") return "—";
      return String(raw).trim();
    }, [effOrder]);

    const displayProjectNo = useMemo(() => {
      if (isRemovalPublicSummary) return "";
      const raw =
        effOrder?.project_number ??
        effOrder?.projectNumber ??
        effOrder?.project?.project_number ??
        effOrder?.project?.projectNumber;
      if (raw == null || String(raw).trim() === "") return "";
      return String(raw).trim();
    }, [effOrder, isRemovalPublicSummary]);

    const selectedTemplate: InvoiceTemplate | null = useMemo(() => {
      if (!Array.isArray(templates)) return null;
      return templates[0] ?? null;
    }, [templates]);

    const localLogoRaw = String(
      selectedTemplate?.images?.url ||
      selectedTemplate?.company_logo?.url ||
      companyLogo ||
      effOrder?.company_logo?.url ||
      effOrder?.images?.url ||
      effOrder?.logo_url ||
      effOrder?.logo ||
      ""
    ).trim();
    const logoRaw = localLogoRaw;
    const logoFileIdForPdf = useMemo(() => {
      const localId = extract24HexId(localLogoRaw);
      if (localId) return localId;
      const rawId = extract24HexId(logoRaw);
      if (rawId) return rawId;
      return "";
    }, [localLogoRaw, logoRaw]);
    const logoFileIds = useMemo(() => {
      const id = extract24HexId(logoRaw);
      return id ? [id] : [];
    }, [logoRaw]);
    const logoSyntheticRows = useMemo(
      () => (logoFileIds.length ? [{ images: logoFileIds.map((id) => ({ url: id })) }] : []),
      [logoFileIds]
    );
    const logoImgMap = useVariantImageUrlMap(logoSyntheticRows, presignImageToken) ?? {};
    const resolvedCompanyLogo = useMemo(() => {
      const v = logoRaw;
      if (!v) return "";
      if (/^(https?:|data:|blob:)/i.test(v)) return v;
      const mergedMap = { ...(variantImgMap || {}), ...(logoImgMap || {}) };
      const resolved = (mergedMap[v] || (getImageSrc(v, mergedMap) as string | undefined) || "").trim();
      if (resolved) return resolved;
      // InvoiceDocument-like fallback path for file-id logos.
      if (is24HexId(v)) {
        const directDownload = buildWorkDriveDownloadUrl(v);
        if (directDownload) return directDownload;
      }
      if (logoFileIdForPdf) {
        const directDownload = buildWorkDriveDownloadUrl(logoFileIdForPdf);
        if (directDownload) return directDownload;
      }
      return "";
    }, [logoRaw, variantImgMap, logoImgMap, logoFileIdForPdf]);
    const displayCompanyName = String(
      selectedTemplate?.company_name ||
      effOrder?.signage_company_name ||
      ""
    )
      .replace(/^"|"$/g, "")
      .trim();
    const displayFooterPhone = String(selectedTemplate?.phone_number || footerPhone || "").trim();
    const displayFooterEmail = String(selectedTemplate?.email || footerEmail || "").trim();
    const displayFooterAddress = String(selectedTemplate?.address || footerAddress || "").trim();
    const displayFooterWebsite = String(selectedTemplate?.website || footerWebsite || "").trim();
    const displayTermsText = String(selectedTemplate?.custom_message || termsText || "").trim();
    const displayQuoteNumber = String(displayOrder?.order_number || quoteNumber || "");
    const orderLinkedProjectId = String(
      displayOrder?.project_id ||
      displayOrder?.project?.id ||
      query.get("project_id") ||
      ""
    ).trim();
    const projectProofToken = String(getAuthCookie("sessionToken") || effectiveAccessToken || "").trim();

    useEffect(() => {
      let cancelled = false;
      if (!orderLinkedProjectId || !projectProofToken) {
        setHasApprovedMessage(false);
        setApprovedMessageData(null);
        setProjectDetailMeta({
          approvedBy: "",
          approvedAt: "",
          assignedTo: "",
          shippingName: "",
          shippingDesc: "",
          shippingAmount: null,
        });
        return;
      }
      (async () => {
        try {
          const approved = await fetchProjectApprovedMessage(projectProofToken, orderLinkedProjectId);
          if (cancelled) return;
          const hasMessage = Boolean(approved?.has_approved_message);
          setHasApprovedMessage(hasMessage);
          const data = hasMessage ? (approved?.data ?? null) : null;
          setApprovedMessageData(data);
          if (data) {
            setProjectDetailMeta({
              approvedBy: String((data as any)?.design_approved_by?.name ?? (data as any)?.approved_by_name ?? "").trim(),
              approvedAt: String((data as any)?.design_approved_at ?? (data as any)?.approved_at ?? "").trim(),
              assignedTo: String((data as any)?.assigned_to_name ?? "").trim(),
              shippingName: String((data as any)?.shipping_method?.name ?? "").trim(),
              shippingDesc: String((data as any)?.shipping_method?.description ?? "").trim(),
              shippingAmount: Number.isFinite(Number((data as any)?.shipping_amount))
                ? Number((data as any)?.shipping_amount)
                : null,
            });
          } else {
            setProjectDetailMeta({
              approvedBy: "",
              approvedAt: "",
              assignedTo: "",
              shippingName: "",
              shippingDesc: "",
              shippingAmount: null,
            });
          }
        } catch {
          if (cancelled) return;
          setHasApprovedMessage(false);
          setApprovedMessageData(null);
          setProjectDetailMeta({
            approvedBy: "",
            approvedAt: "",
            assignedTo: "",
            shippingName: "",
            shippingDesc: "",
            shippingAmount: null,
          });
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [orderLinkedProjectId, projectProofToken, displayOrder]);

    const approvedProofCards = useMemo(() => {
      const approvedRoot = (approvedMessageData as any) || {};
      const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
      if (approvedFiles.length === 0) return [];

      const grouped = new Map<number, any[]>();
      approvedFiles.forEach((f: any) => {
        const version = Number(f?.version);
        const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(f);
      });

      return Array.from(grouped.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([version, files]) => {
          const first = files[0] || {};
          const approvedProofFiles = files.map((f: any, idx: number) => {
            const previewRaw = String(f?.preview_url ?? "").trim();
            const fileRaw = String(f?.file_url ?? f?.url ?? "").trim();
            const previewUrl = previewRaw ? toAbsoluteAssetUrl(previewRaw) || previewRaw : "";
            const href = previewUrl || (fileRaw ? toAbsoluteAssetUrl(fileRaw) || fileRaw : "");
            return {
              id: String(f?.id ?? f?._id ?? idx),
              name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
              href,
              previewUrl: previewUrl || href,
              quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
              sizeLabel: Number.isFinite(Number(f?.file_size)) ? `${Math.round(Number(f?.file_size) / 1024)} KB` : "—",
            };
          });
          const approvedBy = String(
            first?.design_approved_by?.name ||
            first?.updated_by?.name ||
            projectDetailMeta.approvedBy ||
            displayOrder?.design_approved_by?.name ||
            displayOrder?.project?.design_approved_by?.name ||
            ""
          ).trim();
          const approvedAt = dateTimeLabel(
            first?.design_approved_at ||
            first?.updated_at ||
            projectDetailMeta.approvedAt ||
            displayOrder?.design_approved_at ||
            displayOrder?.project?.design_approved_at
          );
          const assignedToName = String(
            projectDetailMeta.assignedTo ||
            displayOrder?.assigned_to_name ||
            displayOrder?.project?.assigned_to_name ||
            ""
          ).trim();
          const approvedShippingName = String(
            first?.shipping_method?.name ||
            projectDetailMeta.shippingName ||
            displayOrder?.shipping_method?.name ||
            ""
          ).trim();
          const approvedShippingDesc = String(
            first?.shipping_method?.description ||
            projectDetailMeta.shippingDesc ||
            displayOrder?.shipping_method?.description ||
            ""
          ).trim();
          const shippingAmountValue = Number.isFinite(Number(first?.shipping_amount))
            ? Number(first?.shipping_amount)
            : Number.isFinite(Number(projectDetailMeta.shippingAmount))
              ? Number(projectDetailMeta.shippingAmount)
              : Number.isFinite(Number(displayOrder?.shipping_amount))
                ? Number(displayOrder?.shipping_amount)
                : NaN;
          const approvedShippingAmount = Number.isFinite(shippingAmountValue)
            ? `$${shippingAmountValue.toFixed(2)}`
            : "";
          const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
          return {
            version,
            files: approvedProofFiles,
            approvedBy,
            approvedAt,
            assignedToName,
            approvedShippingName,
            approvedShippingDesc,
            approvedShippingAmount,
            orderSummary,
          };
        });
    }, [approvedMessageData, projectDetailMeta, displayOrder]);

    const downloadSummaryPdf = useCallback(async () => {
      if (downloadingPdf) return;
      const tokenForPdf = String(
        effectiveAccessToken ||
        getAuthCookie("sessionToken") ||
        ""
      ).trim();
      const resolvedOrderId = String(
        queryOrderId ||
        publicOrder?._id ||
        publicOrder?.id ||
        publicOrder?.order_id ||
        ""
      ).trim();
      if (!resolvedOrderId || !tokenForPdf) return;

      setDownloadingPdf(true);
      try {
        const fileLabel = String(displayQuoteNumber || resolvedOrderId || "order").trim();
        const attachment = isRemovalPublicSummary
          ? await buildRemovalOrderPdfAttachment({
            removalOrderId: resolvedOrderId,
            token: tokenForPdf,
            fileLabel,
          })
          : isInstallationPublicSummary
            ? await buildInstallationOrderPdfAttachment({
              installationOrderId: resolvedOrderId,
              token: tokenForPdf,
              fileLabel,
            })
            : await buildSalesOrderPdfAttachment({
              salesOrderId: resolvedOrderId,
              token: tokenForPdf,
              fileLabel,
            });
        const out = attachmentMapToBlob(attachment);
        if (!out) return;
        const url = URL.createObjectURL(out.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = out.name || "Order-Summary.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } finally {
        setDownloadingPdf(false);
      }
    }, [
      downloadingPdf,
      effectiveAccessToken,
      queryOrderId,
      publicOrder,
      displayQuoteNumber,
      isRemovalPublicSummary,
      isInstallationPublicSummary,
    ]);

    const paymentCreatedBy = useMemo(() => {
      const po = publicOrder as Record<string, unknown> | null | undefined;
      const cb = po?.created_by as { id?: unknown; name?: string } | undefined;
      const idFromCreated = cb && typeof cb === "object" ? normalizeOrderUserId(cb.id) : "";
      const nameFromCreated = cb && typeof cb === "object" ? String(cb.name || "").trim() : "";
      const signageName = String(po?.signage_company_name || "")
        .replace(/^"|"$/g, "")
        .trim();
      return {
        id:
          idFromCreated ||
          normalizeOrderUserId(po?.signage_id) ||
          normalizeOrderUserId(po?.seller_owner_node_id) ||
          String(stripeGatewayUserId || "").trim(),
        name: nameFromCreated || signageName || companyName || "Unknown User",
      };
    }, [publicOrder, stripeGatewayUserId, companyName]);

    /** Payer on public payment POST — sales customer or installation/removal job contact. */
    const paymentPaidBy = useMemo(() => {
      const po = publicOrder as Record<string, unknown> | null | undefined;
      const job = po as Record<string, any> | null | undefined;

      if (isJobOrderSummary && job) {
        const id =
          normalizeOrderUserId(job?.agent_id) ||
          normalizeOrderUserId(job?.team_member_id) ||
          normalizeOrderUserId(job?.customer_id);
        const name = String(
          job?.user_name ||
          job?.agent_name ||
          job?.team_member_name ||
          [job?.customer_first_name, job?.customer_last_name].filter(Boolean).join(" ") ||
          [job?.billing_address?.first_name, job?.billing_address?.last_name].filter(Boolean).join(" ") ||
          ""
        ).trim();
        if (id || name) {
          return { id, name: name || "Customer" };
        }
      }

      const first = String(po?.customer_first_name || "").trim();
      const last = String(po?.customer_last_name || "").trim();
      const fullName = [first, last].filter(Boolean).join(" ").trim();
      return {
        id: normalizeOrderUserId(po?.customer_id),
        name: fullName || first || last || "",
      };
    }, [publicOrder, isJobOrderSummary]);

    const displayQuoteDate = publicOrder?.created_at
      ? new Date(publicOrder.created_at).toLocaleDateString()
      : quoteDate;

    const orderDateMdY = useMemo(() => {
      if (publicOrder?.created_at) {
        const d = new Date(publicOrder.created_at);
        if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
      }
      if (quoteDate) {
        const d = new Date(quoteDate);
        if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
      }
      return "—";
    }, [publicOrder?.created_at, quoteDate]);
    const displayBrokerage = String(publicOrder?.brokerage_firm_name ?? "").trim();
    const displayOffice = String(publicOrder?.office?.officeName ?? "").trim();
    const salesAgentId = normalizeOrderUserId(publicOrder?.agent_id);
    const salesTeamMemberId = normalizeOrderUserId(publicOrder?.team_member_id);
    const salesAgentOrTeamMemberLabel =
      salesAgentId && salesTeamMemberId
        ? "Agent Name"
        : salesTeamMemberId && !salesAgentId
          ? "Team Member Name"
          : "Agent Name";
    const salesCustomerFullName = [
      String(publicOrder?.customer_first_name ?? "").trim(),
      String(publicOrder?.customer_last_name ?? "").trim(),
    ]
      .filter(Boolean)
      .join(" ");
    const jobCustomerUserName = String(publicOrder?.user_name ?? "").trim();
    const displayAgentOrCustomerName = isJobOrderSummary
      ? jobCustomerUserName
      : salesCustomerFullName;
    const displayEmail = String(
      isJobOrderSummary
        ? publicOrder?.user_email ?? ""
        : publicOrder?.customer_email ?? ""
    ).trim();
    const displayPhone = String(
      isJobOrderSummary
        ? publicOrder?.user_phone ?? ""
        : publicOrder?.customer_phone ?? ""
    ).trim();
    const summaryOrderType: InvoiceType = isJobOrderSummary
      ? isInstallationPublicSummary
        ? "installation"
        : "removal"
      : "sales";
    const displayBillingAddress: SummaryAddress | undefined = publicOrder?.billing_address?.address_details
      ? {
        name: String(publicOrder?.billing_address?.first_name || publicOrder?.billing_address?.label || ""),
        phone: String(publicOrder?.billing_address?.address_details?.phone || ""),
        address1: String(publicOrder?.billing_address?.address_details?.addressLine1 || ""),
        address2: String(publicOrder?.billing_address?.address_details?.addressLine2 || ""),
        city: String(publicOrder?.billing_address?.address_details?.city || ""),
        state: String(publicOrder?.billing_address?.address_details?.state || ""),
        zip: String(publicOrder?.billing_address?.address_details?.zip || ""),
        country: String(publicOrder?.billing_address?.address_details?.country || ""),
      }
      : billingAddress;
    const installShipNode = getJobServiceShipNode(io, isInstallationPublicSummary, isRemovalPublicSummary);
    const displayShippingAddress: SummaryAddress | undefined = installShipNode?.address_details
      ? {
        name: String(installShipNode?.first_name || installShipNode?.label || ""),
        phone: String(installShipNode?.address_details?.phone || ""),
        address1: String(installShipNode?.address_details?.addressLine1 || ""),
        address2: String(installShipNode?.address_details?.addressLine2 || ""),
        city: String(installShipNode?.address_details?.city || ""),
        state: String(installShipNode?.address_details?.state || ""),
        zip: String(installShipNode?.address_details?.zip || ""),
        country: String(installShipNode?.address_details?.country || ""),
      }
      : publicOrder?.shipping_address?.address_details
        ? {
          name: String(publicOrder?.shipping_address?.first_name || publicOrder?.shipping_address?.label || ""),
          phone: String(publicOrder?.shipping_address?.address_details?.phone || ""),
          address1: String(publicOrder?.shipping_address?.address_details?.addressLine1 || ""),
          address2: String(publicOrder?.shipping_address?.address_details?.addressLine2 || ""),
          city: String(publicOrder?.shipping_address?.address_details?.city || ""),
          state: String(publicOrder?.shipping_address?.address_details?.state || ""),
          zip: String(publicOrder?.shipping_address?.address_details?.zip || ""),
          country: String(publicOrder?.shipping_address?.address_details?.country || ""),
        }
        : shippingAddress;
    const mapLineItem = (it: any, idx: number): SummaryItem => {
      const quantity = isJobOrderSummary
        ? resolveJobLineItemQuantity(it)
        : Number(it?.quantity || 0);
      const taxAmount = Number(it?.item_tax_info?.total_product_tax_amount || 0);
      const useAssignmentLinePricing =
        !isJobOrderSummary || isInstallationPublicSummary;
      const unitRetailPrice = useAssignmentLinePricing
        ? getSalesOrderLineUnitRetailPrice(it)
        : Number(it?.unit_retail_price ?? it?.unit_price ?? 0);
      const changedUnitPrice = useAssignmentLinePricing
        ? getSalesOrderLineChangedUnitPrice(it)
        : Number(it?.changed_unit_price ?? it?.unit_price ?? 0);
      const lineDiscount = useAssignmentLinePricing
        ? getSalesOrderLineDiscount(it)
        : Number(it?.discount_on_total_quantity ?? it?.discount ?? 0);
      const total = isRemovalPublicSummary
        ? Number(it?.total_price || it?.item_sub_total || 0)
        : useAssignmentLinePricing
          ? getSalesOrderLineTotalAmount(it)
          : Number((quantity * changedUnitPrice + taxAmount).toFixed(2));

      return {
        id: String(it?.variant_id || it?.product_id || idx),
        imageUrl: resolveLineItemImageUrl(it, variantImgMap),
        pdfFileId: (() => {
          const previewId = extract24HexId(it?.preview_url);
          if (previewId) return previewId;
          const rawImageId = extract24HexId(it?.images?.[0]?.url);
          if (rawImageId) return rawImageId;
          const imageId = extract24HexId(it?.image_url);
          if (imageId) return imageId;
          return "";
        })(),
        imageAlt: it?.images?.[0]?.alt_text ? String(it.images[0].alt_text) : undefined,
        productName: String(it?.product_name || ""),
        sku: String(it?.sku || ""),
        details: { ...(it?.attributes || {}), ...(it?.custom_attributes || {}) },
        customizations: it?.customizations ?? null,
        inventorySource: String(it?.inventory_source || ""),
        customInformation: String(it?.custom_information ?? ""),
        quantity,
        unitRetailPrice,
        changedUnitPrice,
        lineDiscount,
        tax: taxAmount,
        total,
      };
    };
    const displayItems: SummaryItem[] =
      isJobOrderSummary && io && Array.isArray(io.line_items)
        ? io.line_items.map(mapLineItem)
        : Array.isArray(publicOrder?.items)
          ? publicOrder.items.map(mapLineItem)
          : items;
    const notesSectionTitle = isJobOrderSummary ? "Instructions" : "Order Notes";
    const notesEmptyText = isJobOrderSummary ? "No notes on this job." : "No notes on this order.";
    const displayNotes = String(
      isJobOrderSummary && Array.isArray(io?.additional_instructions) && io.additional_instructions.length
        ? getLatestAdditionalInstructionValue(io.additional_instructions) || notesEmptyText
        : publicOrder?.notes || orderNotes || notesEmptyText
    );

    const jobScheduledDateLabel = useMemo(() => {
      if (!isJobOrderSummary || !io) return "—";
      const raw = isRemovalPublicSummary
        ? (io as any)?.removal_details?.scheduled_date
        : (io as any)?.installation_details?.scheduled_date;
      return formatJobScheduledDateYmd(raw);
    }, [isJobOrderSummary, isRemovalPublicSummary, io]);

    const jobAssignedToLabel = useMemo(() => {
      if (!isJobOrderSummary || !io) return "—";
      const name = isRemovalPublicSummary
        ? String((io as any)?.removal_details?.installer_name || "").trim()
        : String((io as any)?.installation_details?.installer_name || "").trim();
      return name || "—";
    }, [isJobOrderSummary, isRemovalPublicSummary, io]);

    const jobPriorityLabel = useMemo(() => {
      if (!isJobOrderSummary || !io) return "—";
      return formatJobPriorityLabel((io as any)?.priority, isRemovalPublicSummary);
    }, [isJobOrderSummary, isRemovalPublicSummary, io]);

    const {
      jobBaseServiceCost,
      installationProductSubtotalFromItems,
      installationComputedSubTotal,
      installationSpecialFeesData,
      installationTaxAmount,
      installationDiscountAmount,
      installationGrandTotal,
    } = useMemo(
      () => computeJobOrderTotals(io, isJobOrderSummary, isRemovalPublicSummary),
      [io, isJobOrderSummary, isRemovalPublicSummary]
    );

    const jobDiscountCode = useMemo(
      () => (isJobOrderSummary && io ? resolveInstallationRemovalDiscountCode(io) : ""),
      [isJobOrderSummary, io]
    );

    const salesOrderCharges = useMemo(() => getSalesOrderChargesFirstRow(publicOrder), [publicOrder]);
    const shippingCharges = isJobOrderSummary ? 0 : computeSalesShippingCharges(salesOrderCharges);
    const specialFeeCharges = computeSalesSpecialFeeCharges(salesOrderCharges);

    const salesShippingRowsForTotalsLabel = useMemo(() => {
      const rows = salesOrderCharges?.shipping_option;
      return Array.isArray(rows) ? rows : [];
    }, [salesOrderCharges]);

    const salesIsUpsForTotalsLabel = useMemo(() => {
      const rows = salesOrderCharges?.shipping_option;
      if (!Array.isArray(rows) || !rows[0]) return false;
      const so = rows[0] as any;
      const rawId = String(so?.shipping_id ?? so?.shipping__id ?? "");
      if (rawId && /^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual") return true;
      return /^ups$/i.test(String(so?.shipping_type ?? "").trim());
    }, [salesOrderCharges]);

    const salesSpecialFeeRowsForTotalsLabel = useMemo(() => {
      const rows = salesOrderCharges?.specialfee;
      return Array.isArray(rows) ? rows : [];
    }, [salesOrderCharges]);

    const salesShippingChargeLabel = useMemo(
      () =>
        formatShippingChargeOrderTotalsLabel(salesShippingRowsForTotalsLabel, { isUps: salesIsUpsForTotalsLabel }),
      [salesShippingRowsForTotalsLabel, salesIsUpsForTotalsLabel]
    );
    const salesSpecialFeeLabel = useMemo(
      () => formatSpecialFeeOrderTotalsLabel(salesSpecialFeeRowsForTotalsLabel),
      [salesSpecialFeeRowsForTotalsLabel]
    );

    const jobSpecialFeeRowsForTotalsLabel = useMemo(() => {
      if (!isJobOrderSummary || !io) return [];
      const ch = io?.order_charges?.[0];
      const rows = ch?.specialfee ?? io?.specialfee;
      return Array.isArray(rows) ? rows : [];
    }, [isJobOrderSummary, io?.order_charges, io?.specialfee, io]);

    const jobSpecialFeeLabel = useMemo(
      () => formatSpecialFeeOrderTotalsLabel(jobSpecialFeeRowsForTotalsLabel),
      [jobSpecialFeeRowsForTotalsLabel]
    );

    const salesSubtotalBeforeDiscount = useMemo(() => {
      if (isJobOrderSummary) return 0;
      const rows = Array.isArray(publicOrder?.items) ? publicOrder.items : [];
      if (rows.length) return computeSalesSubtotalBeforeDiscountFromItems(rows);
      return Number(subtotal ?? 0);
    }, [isJobOrderSummary, publicOrder?.items, subtotal]);

    const salesDiscountAmount = useMemo(() => {
      if (isJobOrderSummary) return 0;
      return Math.max(0, Number(publicOrder?.discount_amount ?? 0));
    }, [isJobOrderSummary, publicOrder?.discount_amount]);

    const displaySubtotal = isJobOrderSummary
      ? Number(publicOrder?.subtotal_amount ?? subtotal ?? 0)
      : salesSubtotalBeforeDiscount;
    const displayShipping = isJobOrderSummary
      ? 0
      : shippingCharges || Number(publicOrder?.shipping_amount || shipping || 0);
    const displaySpecialFees = specialFeeCharges || Number(specialFees || 0);
    const displayTax = Number(publicOrder?.order_tax_summary?.over_all_tax_amount ?? tax ?? 0);
    const displayGrandTotal = isJobOrderSummary
      ? installationGrandTotal
      : Number(displaySubtotal + displayTax + displaySpecialFees + displayShipping - salesDiscountAmount);
    /** Sum of prior payments on the public order — same shape as SalesOrderDetails `payment_info`. */
    const publicOrderAmountPaid = useMemo(
      () => sumOrderPaymentInfoAmountPaid(effOrder),
      [effOrder]
    );
    /** Amount still owed on the public link (charge intents and Pay button must use this, not the full grand total). */
    const displayPaymentRemaining = useMemo(() => {
      const r = Number(displayGrandTotal || 0) - Number(publicOrderAmountPaid || 0);
      return Math.max(0, Math.round(r * 100) / 100);
    }, [displayGrandTotal, publicOrderAmountPaid]);

    /** Screen + PDF: match amounts; underscore API values; avoid broken PARTIAL label in html2pdf. */
    const displayPaymentStatus = useMemo(() => {
      const gt = Number(displayGrandTotal || 0);
      const paid = Number(publicOrderAmountPaid || 0);
      const raw = String(publicOrder?.payment_status || paymentStatusLabel || "").trim();
      const normalized = (s: string) => {
        const base = s.replace(/_/g, " ").replace(/\s+/g, " ").trim();
        const compact = base.replace(/\s+/g, "").toLowerCase();
        if (compact === "partialpayment") return "PARTIAL PAYMENT";
        return base;
      };

      const isPartialByAmount = gt > 0 && paid > 0 && paid < gt - 0.005;
      if (isPartialByAmount) {
        return "PARTIAL PAYMENT";
      }

      if (raw) {
        return normalized(raw) || raw;
      }

      if (gt > 0 && paid >= gt - 0.005) return "Paid";
      return "Awaiting Payment";
    }, [publicOrder?.payment_status, paymentStatusLabel, displayGrandTotal, publicOrderAmountPaid]);
    const latestPaymentInfo = useMemo(() => pickLatestOrderPaymentInfo(effOrder), [effOrder]);
    const hasPaymentDetails = Boolean(latestPaymentInfo);
    const billingLines = formatAddressLines(displayBillingAddress);
    const shippingLines = formatAddressLines(displayShippingAddress);
    const payText = payButtonLabel || `Pay ${money(displayPaymentRemaining)}`;
    const effectiveShowPayButton = showPayButton && !isPrivateOrderSummaryPreview;
    const isPublicOrderCancelled = useMemo(
      () =>
        isPublicOrderCancelledForSummary(
          (publicOrder as { status?: number | string } | null)?.status,
          isRemovalPublicSummary,
          isInstallationPublicSummary
        ),
      [publicOrder?.status, isInstallationPublicSummary, isRemovalPublicSummary]
    );
    const isPaymentSettledForPublicPay = useMemo(() => {
      const ps = String(publicOrder?.payment_status || paymentStatusLabel || "").trim().toLowerCase();
      return ps === "paid" || ps === "overpaid";
    }, [publicOrder?.payment_status, paymentStatusLabel]);

    const shouldShowPublicPayUi =
      effectiveShowPayButton &&
      !isPaymentSettledForPublicPay &&
      !isPublicOrderCancelled &&
      displayPaymentRemaining > 0;

    const publicOrderStatusCode = Number(publicOrder?.status ?? 0);
    // Sales: Draft=100 → Approve; Confirmed(101)+ already finalized → Pay balance only.
    // Install/removal: finalized at 200+.
    const isOrderFinalizedForPublicApprove = isRemovalPublicSummary || isInstallationPublicSummary
      ? publicOrderStatusCode >= 200
      : publicOrderStatusCode >= SALES_ORDER_CONFIRMED_STATUS_CODE;
    const shouldShowApproveButton =
      userHasPaymentMethods && shouldShowPublicPayUi && !isOrderFinalizedForPublicApprove;

    const supplierIdForPaymentMethodsCount = String(
      supplierUserIdForPaymentBranding || stripeGatewayUserId || ""
    ).trim();
    const userIdForPaymentMethodsCount = String(paymentPaidBy.id || "").trim();
    const orderIdForFinalize = String(
      publicOrder?._id || publicOrder?.id || publicOrder?.order_id || queryOrderId || ""
    ).trim();

    const publicOrderKindForFinalize: "sales" | "installation" | "removal" = isRemovalPublicSummary
      ? "removal"
      : isInstallationPublicSummary
        ? "installation"
        : "sales";

    useEffect(() => {
      if (!shouldShowPublicPayUi || isPrivateOrderSummaryPreview) {
        setUserHasPaymentMethods(false);
        setApproveError(null);
        return;
      }
      if (!supplierIdForPaymentMethodsCount || !userIdForPaymentMethodsCount) {
        setUserHasPaymentMethods(false);
        return;
      }

      let mounted = true;
      setLoadingPaymentMethodsCount(true);
      setApproveError(null);
      (async () => {
        try {
          const result = await fetchPublicPaymentMethodsCountBySupplierAndUser(
            supplierIdForPaymentMethodsCount,
            userIdForPaymentMethodsCount
          );
          if (!mounted) return;
          setUserHasPaymentMethods(Boolean(result.has_payment_methods));
        } catch {
          if (!mounted) return;
          setUserHasPaymentMethods(false);
        } finally {
          if (mounted) setLoadingPaymentMethodsCount(false);
        }
      })();

      return () => {
        mounted = false;
      };
    }, [
      shouldShowPublicPayUi,
      isPrivateOrderSummaryPreview,
      supplierIdForPaymentMethodsCount,
      userIdForPaymentMethodsCount,
    ]);

    const refreshPublicOrder = useCallback(async () => {
      if (!queryOrderId) return null;
      const normalized = await fetchNormalizedPublicOrderForSummary(
        isRemovalPublicSummary,
        isInstallationPublicSummary,
        effectiveProjectId,
        queryOrderId,
        effectiveAccessToken
      );
      setPublicOrder(normalized);
      return normalized;
    }, [
      queryOrderId,
      isRemovalPublicSummary,
      isInstallationPublicSummary,
      effectiveProjectId,
      effectiveAccessToken,
    ]);

    const handleApproveOrder = useCallback(async () => {
      if (!orderIdForFinalize || finalizingOrder) return;
      setApproveError(null);
      setFinalizingOrder(true);
      try {
        await finalizePublicOrderForSummary(publicOrderKindForFinalize, orderIdForFinalize);
        setUserHasPaymentMethods(false);
        await refreshPublicOrder();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to approve order";
        setApproveError(msg);
      } finally {
        setFinalizingOrder(false);
      }
    }, [
      orderIdForFinalize,
      finalizingOrder,
      publicOrderKindForFinalize,
      refreshPublicOrder,
    ]);

    useEffect(() => {
      if (isPrivateOrderSummaryPreview && effectivePreviewState?.order) {
        setPublicOrder(effectivePreviewState.order);
        return;
      }
      // if (!queryOrderId || !effectiveProjectId) return;
      if (!queryOrderId) return;
      let mounted = true;
      (async () => {
        try {
          const normalized = await fetchNormalizedPublicOrderForSummary(
            isRemovalPublicSummary,
            isInstallationPublicSummary,
            effectiveProjectId,
            queryOrderId,
            effectiveAccessToken
          );
          setPublicOrder(normalized);
        } catch {
          if (mounted) setPublicOrder(null);
        }
      })();
      return () => {
        mounted = false;
      };
    }, [
      isPrivateOrderSummaryPreview,
      effectivePreviewState?.order,
      effectiveProjectId,
      queryOrderId,
      effectiveAccessToken,
      isInstallationPublicSummary,
      isRemovalPublicSummary,
    ]);

    const openPaymentModal = useCallback(async () => {
      // if (!queryOrderId || !effectiveAccessToken) return;
      //  if (!queryOrderId) return;
      setShowPayModal(true);
      setPaymentInitError(null);
      if (stripePromise && clientSecret) return;
      try {
        setLoadingPaymentUi(true);

        let publishableKey = String(
          queryPublishableKey ||
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
          process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
          ""
        ).trim();
        if (!publishableKey) {
          const bearer =
            getAuthCookie("sessionToken") || effectiveAccessToken || "";
          if (bearer) {
            try {
              const gateways = await fetchPaymentGatewaysCreatedByUserId(bearer, stripeGatewayUserId);
              const stripeGateway = gateways.find((g: any) => String(g?.gateway || "").toLowerCase() === "stripe");
              publishableKey = String(stripeGateway?.publishable_key || "");
            } catch {
              publishableKey = "";
            }
          }
        }
        if (!publishableKey) {
          setPaymentInitError(
            "Stripe is not configured for this supplier. Wait for the order to finish loading and try again, or add REACT_APP_STRIPE_PUBLISHABLE_KEY for local development."
          );
          return;
        }

        if (!stripePromise) {
          setStripePromise(loadStripe(publishableKey));
        }

        if (queryClientSecret) {
          setClientSecret(queryClientSecret);
          return;
        }

        const intentRes = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${encodeURIComponent(stripeGatewayUserId)}?token=${encodeURIComponent(
            effectiveAccessToken
          )}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount: Math.round(Number(displayPaymentRemaining || 0) * 100),
              currency: "usd",
              order_id: String(queryOrderId || publicOrder?.id || publicOrder?._id || ""),
              order_number: String(displayQuoteNumber || publicOrder?.order_number || publicOrder?.job_number || ""),
              customer_name: String(displayAgentOrCustomerName || ""),
              customer_email: String(displayEmail || ""),
              customer_phone: String(displayPhone || ""),
              payment_method_types: ["card"],
            }),
          }
        );
        const intentJson = await intentRes.json().catch(() => ({}));
        const cs = intentJson?.clientSecret || intentJson?.client_secret || "";
        if (cs) {
          setClientSecret(String(cs));
        } else {
          setPaymentInitError("Could not create a payment session. Please try again.");
        }
      } finally {
        setLoadingPaymentUi(false);
      }
    }, [
      queryOrderId,
      effectiveAccessToken,
      stripePromise,
      clientSecret,
      stripeGatewayUserId,
      queryClientSecret,
      queryPublishableKey,
      displayPaymentRemaining,
      publicOrder,
      displayQuoteNumber,
      displayAgentOrCustomerName,
      displayEmail,
      displayPhone,
    ]);

    useEffect(() => {
      if (!showPayModal) autoStripeRetryRef.current = false;
    }, [showPayModal]);

    /** If Pay opened before the public order loaded, the first init used project_id for the gateway. Retry once when we resolve the real supplier user id. */
    useEffect(() => {
      if (!showPayModal || !queryClientSecret) return;
      if (stripePromise && clientSecret) return;
      if (autoStripeRetryRef.current) return;
      if (cleanParam(query.get("supplier_id"))) return;
      if (stripeGatewayUserId === queryProjectId) return;
      autoStripeRetryRef.current = true;
      void openPaymentModal();
    }, [showPayModal, queryClientSecret, stripeGatewayUserId, queryProjectId, query, stripePromise, clientSecret, openPaymentModal]);

    const payActionsBlock =
      shouldShowPublicPayUi ? (
        <div className="status100-bottom-pay-block">
          {shouldShowApproveButton ? (
            <>
              <button
                type="button"
                className="status100-approve-btn"
                onClick={() => void handleApproveOrder()}
                disabled={finalizingOrder || loadingPaymentMethodsCount}
              >
                {finalizingOrder ? "Approving..." : "Approve"}
              </button>
              {approveError ? (
                <div className="status100-pay-note text-danger">{approveError}</div>
              ) 
              : (
                <></>
                // <div className="status100-pay-note">
                //   Approve this order using your saved payment method on file.
                // </div>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="status100-pay-btn"
                onClick={openPaymentModal}
                disabled={loadingPaymentMethodsCount}
              >
                {loadingPaymentMethodsCount ? "Loading..." : payText}
              </button>
              <div className="status100-pay-note">Secure payments powered by Stripe (256-bit SSL encrypted)</div>
            </>
          )}
        </div>
      ) : null;

    const orderNotesSection = (
      <>
        <div className="status100-section-head">
          <div style={sectionIconBox}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z" />
              <path d="M14 2v6h6" />
            </svg>
          </div>
          <div style={sectionTitle}>{notesSectionTitle}</div>
        </div>
        <div className="status100-card status100-notes-box">{show(displayNotes)}</div>
      </>
    );

    const paymentDetailsCardInner = hasPaymentDetails ? (
      <div className="status100-payment-card">
        <div className="status100-total-heading">Payment Details</div>
        <div className="status100-total-line">
          <span>Transaction ID</span>
          <strong>{show(latestPaymentInfo?.payment_id)}</strong>
        </div>
        <div className="status100-total-line">
          <span>Payment Method</span>
          <strong>{show(latestPaymentInfo?.method)}</strong>
        </div>
        <div className="status100-total-line">
          <span>Paid By</span>
          <strong>{displayPaymentPaidByName(latestPaymentInfo)}</strong>
        </div>
        <div className="status100-grand">
          <div className="status100-grand-label">Amount Paid</div>
          <div className="status100-grand-value">
            {money(Number(latestPaymentInfo?.amount_paid ?? latestPaymentInfo?.total_amount ?? 0))}
          </div>
        </div>
      </div>
    ) : null;

    const orderTotalPaymentCard = (
      <div className="status100-payment-card">
        <div className="status100-total-heading">{isJobOrderSummary ? "Cost Breakdown" : "Order Total"}</div>

        {isJobOrderSummary ? (
          <>
            <div className="status100-total-line">
              <span>{isRemovalPublicSummary ? "Removal Cost:" : "Installation Cost:"}</span>
              <strong>{money(jobBaseServiceCost)}</strong>
            </div>
            {!isRemovalPublicSummary ? (
              <div className="status100-total-line">
                <span>Unit Cost:</span>
                <strong>{money(installationProductSubtotalFromItems)}</strong>
              </div>
            ) : null}
            <div className="status100-total-line">
              <span>Sub-total</span>
              <strong>{money(installationComputedSubTotal)}</strong>
            </div>
            {installationSpecialFeesData.total > 0 ? (
              <div className="status100-total-line">
                <span className="text-break" style={{ maxWidth: "72%" }}>
                  {jobSpecialFeeLabel}
                </span>
                <strong>{money(installationSpecialFeesData.total)}</strong>
              </div>
            ) : null}
            <div className="status100-total-line">
              <span>Tax</span>
              <strong>{money(installationTaxAmount)}</strong>
            </div>
            {installationDiscountAmount > 0 ? (
              <div className="status100-total-line">
                <span>
                  Discount
                  <span style={{ color: "#00a63e" }}>
                    {jobDiscountCode ? `:(${jobDiscountCode})` : ""}
                  </span>
                </span>
                <strong style={{ color: "#dc2626" }}>-{money(installationDiscountAmount)}</strong>
              </div>
            ) : null}
            <div className="status100-grand">
              <div className="status100-grand-label">Grand Total</div>
              <div className="status100-grand-value">{money(displayGrandTotal)}</div>
            </div>
          </>
        ) : (
          <>
            <div className="status100-total-line">
              <span>Sub Total:</span>
              <strong>{money(displaySubtotal)}</strong>
            </div>
            {salesDiscountAmount > 0 ? (
              <div className="status100-total-line">
                <span>
                  Discount
                  <span style={{ color: "#00a63e" }}>
                  {String(publicOrder?.discount_code || "").trim()
                    ? `:(${String(publicOrder?.discount_code).trim()})`
                    : ""}
                    </span>
                </span>
                <strong style={{ color: "#00a63e" }}>-{money(salesDiscountAmount)}</strong>
              </div>
            ) : null}
            <div className="status100-total-line">
              <span className="text-break" style={{ maxWidth: "72%" }}>
                {salesShippingChargeLabel}
              </span>
              <strong>{money(displayShipping)}</strong>
            </div>
            <div className="status100-total-line">
              <span className="text-break" style={{ maxWidth: "72%" }}>
                {salesSpecialFeeLabel}
              </span>
              <strong>{money(displaySpecialFees)}</strong>
            </div>
            <div className="status100-total-line">
              <span>Tax</span>
              <strong>{money(displayTax)}</strong>
            </div>

            <div className="status100-grand">
              <div className="status100-grand-label">Grand Total</div>
              <div className="status100-grand-value">{money(displayGrandTotal)}</div>
            </div>
          </>
        )}

        {(() => {
          const alreadyPaidValue = Number(publicOrderAmountPaid);
          const remainingBalanceValue = Number(displayPaymentRemaining);
          if (!Number.isFinite(alreadyPaidValue) || !Number.isFinite(remainingBalanceValue)) {
            return null;
          }
          return (
            <>
              <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 14, paddingTop: 14 }} />
              <div className="status100-total-line">
                <span>Already Paid:</span>
                <strong style={{ color: "#00a63e" }}>{money(alreadyPaidValue)}</strong>
              </div>
              <div className="status100-total-line">
                <span>Remaining Balance:</span>
                <strong style={{ color: "#f06548" }}>{money(remainingBalanceValue)}</strong>
              </div>
            </>
          );
        })()}
      </div>
    );

    return (
      <div
        className="order-summary-print-root"
        style={{ padding: 16, fontFamily: "Arial, Helvetica, sans-serif" }}
      >
        {isPrivateOrderSummaryPreview ? (
          <div
            className="d-print-none no-print"
            style={{
              maxWidth: 940,
              margin: "0 auto 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#374151",
                }}
              >
                ← Back
              </button>
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>
                {isJobOrderSummary ? "Job Summary Preview" : "Order summary (preview)"}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* <button
              type="button"
              className="btn btn-primary table-addbutton no-print"
              onClick={() => window.print()}
            >
              <i className="bi bi-printer me-2" /> Print/Download
            </button> */}
              <button
                type="button"
                className="btn btn-primary table-addbutton no-print"
                onClick={downloadSummaryPdf}
                disabled={downloadingPdf}
                style={downloadingPdf ? { cursor: "not-allowed", backgroundColor: "#155dfc", borderColor: "#155dfc", color: "#fff", opacity: 1 } : undefined}
              >
                <i className="bi bi-download me-2" /> {downloadingPdf ? "Downloading..." : "Download"}
              </button>
            </div>
          </div>
        ) : null}
        <style>{`
        .status100-shell * { box-sizing: border-box; }
        .status100-shell {
          width: 100%;
          max-width: 940px;
          margin: 0 auto;
          background: #ffffff;
          border: 1px solid #d4dae4;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 12px 34px rgba(15, 23, 42, 0.12);
          color: #111827;
          letter-spacing: 0.012em;
        }
        // .status100-pad { padding: 28px 32px; }
        .status100-pad { padding: 14px 32px 0px 32px; }
        .status100-padcustom { padding: 3px 32px; }
        /* Title + logo + order meta: desktop = centered title row, logo left / meta right below */
        .status100-hero {
          display: grid;
          grid-template-columns: auto 1fr auto;
          grid-template-rows: auto auto;
          column-gap: 20px;
          row-gap: 10px;
          align-items: start;
          position: relative;
        }
        .status100-hero-title-wrap {
          grid-column: 1 / -1;
          justify-self: center;
          text-align: center;
          width: 100%;
        }
        .status100-hero .status100-brand {
          grid-column: 1;
          grid-row: 2;
          justify-self: start;
          align-self: center;
        }
        .status100-hero .status100-header-right {
          grid-column: 3;
          grid-row: 2;
          justify-self: end;
        }
        .status100-brand img {
          max-width: 220px;
          max-height: 60px;
          object-fit: contain;
          display: block;
        }
        .status100-brand-name {
          font-size: 28px;
          font-weight: 700;
          letter-spacing: 0.005em;
        }
        .status100-header-right {
          min-width: 270px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
        .status100-title {
          font-size: 22px;
          font-weight: 700;
          line-height: 1.1;
        }
        .status100-title-subtitle {
          display: block;
          margin-top: 6px;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.02em;
          // color: #6b7280;
          line-height: 1.3;
          text-align: center;
          white-space: normal;
        }
        .status100-title--page-center {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: max-content;
          text-align: center;
          white-space: nowrap;
        }
        .status100-meta-grid {
          display: grid;
          grid-template-columns: auto auto;
          column-gap: 12px;
          row-gap: 8px;
          align-items: baseline;
          justify-content: end;
          width: max-content;
          max-width: 100%;
          margin-left: auto;
        }
        .status100-meta-grid .status100-meta-label {
          text-transform: none;
          letter-spacing: normal;
          justify-self: start;
        }
        .status100-meta-grid .status100-meta-value {
          font-size: 13px;
          font-weight: 500;
          color: #111827;
          text-align: right;
          justify-self: end;
          min-width: 0;
        }
        .status100-meta-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .status100-meta-label {
            font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;font-weight: 500;
          // text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .status100-meta-value {
          font-size: 13px;
          font-weight: 500;
          color: #111827;
        }
        .status100-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 7px 14px;
          border-radius: 8px;
          background: #00a63e !important;
          color: #ffffff;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: normal;
          white-space: nowrap;
          max-width: max-content;
          line-height: 1.25;
        }
        .status100-section { margin-top: 26px; }
        .status100-section-head {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }
        .status100-card {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #fafbfd;
        }
        .status100-customer-box {
          padding: 20px;
        }
        .status100-customer-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px 22px;
        }
        .status100-customer-grid > div {
          min-width: 0;
        }
        .status100-email-cell {
          min-width: 0;
          max-width: 100%;
        }
        .status100-address-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .status100-address-card {
          position: relative;
          padding: 18px 18px 18px 20px;
          min-height: 150px;
          overflow: hidden;
        }
        .status100-address-card::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          width: 3px;
          height: 100%;
          // background: rgba(59, 111, 232, 0.32);
        }
        .status100-address-label {
          margin-bottom: 10px;
          font-size: 0.875rem;
          color: #111827;
          letter-spacing: 0.01em;
          font-family: Roboto, sans-serif;
          font-weight: 500 !important;
        }
        .status100-address-line {
          line-height: 1.75;
          font-size: 0.875rem;
          color: #111827;
          letter-spacing: 0.01em;
          font-family: Roboto, sans-serif;
        }

        .status100-address-line.is-name {
          font-weight: 700;
          color: #111827;
        }
        .status100-job-details-card {
          padding: 18px 20px;
        }
        .status100-job-details-row {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          flex-wrap: nowrap;
        }
        .status100-job-details-col {
          flex: 1 1 0;
          min-width: 0;
        }
        .status100-job-priority-pill {
          display: inline-block;
          margin-top: 2px;
          padding: 4px 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          background: #e0f2fe;
          color: #0369a1;
        }
        .status100-table-wrap {
          overflow-x: auto;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #ffffff;
        }
        #order-summary-print .invoice-pdf-items-table,
        .order-summary-print-root .invoice-pdf-items-table {
          width: 100%;
          min-width: 0;
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed;
        }
        @media screen and (max-width: 1400px) {
          #order-summary-print .invoice-pdf-items-table,
          .order-summary-print-root .invoice-pdf-items-table {
            min-width: 980px;
          }
        }
        #order-summary-print .invoice-pdf-col-image {
          width: 10%;
          text-align: left;
          vertical-align: top;
          padding-left: 4;
          padding-right: 10px;
        }
        #order-summary-print .invoice-pdf-image-cell-wrap {
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          width: 100%;
        }
        #order-summary-print .invoice-pdf-col-product,
        .order-summary-print-root .invoice-pdf-col-product {
          width: 18%;
          text-align: left;
          padding-left: 14px;
        }
        #order-summary-print .invoice-pdf-col-details,
        .order-summary-print-root .invoice-pdf-col-details {
          width: 14%;
          text-align: left;
        }
        #order-summary-print .invoice-pdf-col-qty,
        .order-summary-print-root .invoice-pdf-col-qty {
          width: 6%;
          text-align: center;
          vertical-align: top;
        }
        #order-summary-print .invoice-pdf-col-unit,
        .order-summary-print-root .invoice-pdf-col-unit {
          width: 9%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        #order-summary-print .invoice-pdf-col-changed,
        .order-summary-print-root .invoice-pdf-col-changed {
          width: 9%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        #order-summary-print .invoice-pdf-col-discount,
        .order-summary-print-root .invoice-pdf-col-discount {
          width: 9%;
          text-align: center;
          // vertical-align: middle;
        }
        #order-summary-print .invoice-pdf-col-tax,
        .order-summary-print-root .invoice-pdf-col-tax {
          width: 8%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        #order-summary-print .invoice-pdf-col-total,
        .order-summary-print-root .invoice-pdf-col-total {
          width: 10%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        .status100-table {
          width: 100%;
          min-width: 760px;
          border-collapse: collapse;
          // table-layout: fixed;
        }
        .status100-table thead tr { background: #f5f7fb; }
        .status100-table th {
          padding: 12px 14px;
          text-align: left;
          font-weight: 700;
          border-bottom: 1px solid #e5e7eb;
          font-size: 0.875rem;
          line-height: 1.75;
          color: #111827;
          letter-spacing: 0.01em;
          font-family: Roboto, sans-serif;
        }
        .status100-table tbody tr {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .status100-table td {
          padding: 16px 14px;
          border-bottom: 1px solid #f0f2f5;
          vertical-align: top;
          font-size: 14px;
          color: #374151;
          min-width: 0;
        }
        .status100-table th:nth-child(1),
        .status100-table td:nth-child(1) {
          padding-right: 14px;
        }
        .status100-table th:nth-child(1),
        .status100-table td:nth-child(1) {
          width:11%;
        }
        .status100-table th:nth-child(2),
        .status100-table td:nth-child(2) {
          width: 24%;
          padding-left: 14px;
        }
        .status100-table th:nth-child(3),
        .status100-table td:nth-child(3) {
          width: 24%;
        }
        .status100-table th:nth-child(4),
        .status100-table td:nth-child(4) {
          width: 8%;
          text-align: center;
        }
        .status100-table th:nth-child(5),
        .status100-table td:nth-child(5) {
          width: 12%;
          text-align: right;
        }
        .status100-table th:nth-child(6),
        .status100-table td:nth-child(6) {
          width: 10%;
          text-align: center;
        }
        .status100-table th:nth-child(7),
        .status100-table td:nth-child(7) {
          width: 13%;
          text-align: right;
        }
        .status100-table.invoice-pdf-items-table {
          table-layout: fixed;
          width: 100%;
        }
        .status100-table.invoice-pdf-items-table thead th {
          vertical-align: middle;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-image,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-image {
          width: 10%;
          text-align: left;
          vertical-align: top;
          padding-left: 0;
          padding-right: 10px;
        }
        .status100-table.invoice-pdf-items-table .invoice-pdf-image-cell-wrap {
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          width: 100%;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-product,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-product {
          width: 18%;
          text-align: left;
          padding-left: 14px;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-details,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-details {
          width: 14%;
          text-align: left;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-qty,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-qty {
          width: 6%;
          text-align: center;
          vertical-align: top;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-changed,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-changed {
          width: 9%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        .status100-table.invoice-pdf-items-table thead th.invoice-pdf-col-changed {
          white-space: normal;
          line-height: 1.15;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-discount,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-discount {
          width: 9%;
          text-align: center;
          vertical-align: middle;
        }
        .status100-table.invoice-pdf-items-table th.invoice-pdf-col-total,
        .status100-table.invoice-pdf-items-table td.invoice-pdf-col-total {
          width: 10%;
          text-align: right;
          vertical-align: top;
          white-space: nowrap;
        }
        .status100-table td:nth-child(2),
        .status100-table td:nth-child(3) {
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .status100-table td:nth-child(3) .status100-product-detail {
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .status100-table tbody tr:last-child td { border-bottom: none; }
        .status100-right { text-align: right;
        //  color : #4b5563;
             font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
    font-weight: 500;
          }
        .status100-center { text-align: center; }
        .status100-qty { text-align: center; color : #4b5563; }
        .status100-product-cell {
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }
        .status100-thumb {
          width: 80px;
          height: 80px;
          max-width: 100%;
          border-radius: 8px;
          // border: 1px solid #e5e7eb;
          // background: #f9fafb;
          object-fit: contain;
          flex-shrink: 0;
          box-sizing: border-box;
        }
        .status100-thumb-fallback {
          width: 52px;
          height: 52px;
          max-width: 100%;
          box-sizing: border-box;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
          line-height: 1.2;
          text-align: center;
          padding: 4px;
          flex-shrink: 0;
          word-break: break-word;
        }
        .status100-product-name {
          font-size: 15px;
          font-weight: 700;
          color: #495363 !important;
          line-height: 1.4;
          letter-spacing: 0.01em;
          display: block;
          min-width: 0;
          max-width: 100%;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .status100-product-sku {
          // font-size: 12px;
          // color: #495363 !important;
          margin-top: 4px;
          // letter-spacing: 0.01em;

              font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
    font-weight: 500;
          display: block;
          min-width: 0;
          max-width: 100%;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .status100-product-detail {
             font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
          line-height: 1.6;
          // letter-spacing: 0.01em;
          min-width: 0;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .status100-total-bold {
          font-weight: 700;
          color: #111827;
        }
        /* Bottom: Bootstrap row/col; Pay sits under Order Total in the right column */
        .status100-shell .status100-bottom {
          margin-top: 24px;
          width: 100%;
          box-sizing: border-box;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .status100-shell .status100-bottom.status100-bottom-has-payment {
          display: block;
        }
        .status100-bottom-notes-col {
          min-width: 0;
        }
        .status100-bottom-totals-col {
          min-width: 0;
        }
        .status100-bottom-pay-block {
          width: 100%;
          box-sizing: border-box;
        }
        .status100-bottom-pay-block .status100-pay-btn {
          margin-top: 0;
        }
        .status100-bottom.row .status100-payment-card {
          max-width: 100%;
        }
        .status100-notes-box {
          background: #ffffff;
          padding: 18px;
          min-height: 84px;
          width: 100%;
          max-width: none;
          box-sizing: border-box;
          word-break: normal;
          overflow-wrap: break-word;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
        }
        .status100-payment-card {
          background: #ffffff;
          border: 1px solid #dde3ed;
          border-radius: 10px;
          padding: 22px;
          box-shadow: 0 8px 28px rgba(15, 23, 42, 0.10);
          max-width: 100%;
          box-sizing: border-box;
        }
        .status100-total-heading {
          font-weight: 700;
          padding-bottom: 12px;
          border-bottom: 1px solid #edf0f4;
          margin-bottom: 2px;

          font-size: 0.975rem;
    color: rgb(17, 24, 39);
    letter-spacing: 0.01em;
    font-family: Roboto, sans-serif;
    font-weight: 700;
        }
        .status100-total-line {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 10px 0;
          border-bottom: 1px solid #edf0f4;
          // font-size: 14px;
          // color: #4b5563;
          font-weight: 500;

              font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
        }
        .status100-grand {
          margin-top: 14px;
          padding: 14px 16px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f8faff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }
        .status100-grand-label {
          font-size: 14px;
          font-weight: 700;
          color: #111827;
        }
        .status100-grand-value {
          font-size: 18px;
          font-weight: 700;
          color: #3b6fe8;
        }
        .status100-pay-btn {
          display: block;
          width: 100%;
          margin-top: 14px;
          text-align: center;
          text-decoration: none;
          border-radius: 10px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          box-shadow: 0 6px 18px rgba(34, 197, 94, 0.22);
          border: none;
          cursor: pointer;
        }
        .status100-pay-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .status100-approve-btn {
          display: block;
          width: 100%;
          margin-top: 14px;
          text-align: center;
          text-decoration: none;
          border-radius: 10px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          box-shadow: 0 6px 18px rgba(37, 99, 235, 0.22);
          border: none;
          cursor: pointer;
        }
        .status100-approve-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .status100-pay-note,
        .status100-contact-note {
          text-align: center;
              font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
    font-weight: 500;
          margin-top: 12px;
          line-height: 1.5;
        }
        .status100-contact-note a,
        .status100-email-link,
        .status100-footer-link {
          color: #3b6fe8;
          text-decoration: none;
          overflow-wrap: anywhere;
          word-break: break-word;
          max-width: 100%;
        }
        .status100-contact-note a:hover,
        .status100-email-link:hover,
        .status100-footer-link:hover {
          text-decoration: none;
          color: #2563eb;
        }
        .status100-powered-lead {
              font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
        }
        .status100-powered-brand {
          color: #2563eb;
          font-weight: 600;
          text-decoration: none;
        }
        .status100-powered-brand:hover {
          text-decoration: none;
          color: #2563eb;
        }
        .status100-footer {
          margin-top: 28px;
          border-top: 1px solid #e5e7eb;
          background: #fafbfd;
          padding: 24px 32px 26px;
        }
        .invoice-footer-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px 28px;
          margin-bottom: 16px;
        }
        .invoice-footer-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }
        .invoice-footer-field-address {
          grid-column: span 2;
        }
        .invoice-footer-k {
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-family: Roboto, sans-serif;
        }
        .invoice-footer-v {
          font-size: 0.875rem;
          font-weight: 500;
          color: #111827;
          line-height: 1.55;
          word-break: break-word;
          overflow-wrap: anywhere;
          font-family: Roboto, sans-serif;
        }
        .invoice-footer-website .status100-footer-link {
          word-break: break-all;
        }
        .status100-terms {
          margin-top: 12px;
          border-top: 1px solid #e5e7eb;
          padding-top: 16px;
        }
        .status100-terms-label {
          margin-bottom: 8px;

           font-size: 0.975rem;
    color: rgb(17, 24, 39);
    letter-spacing: 0.01em;
    font-family: Roboto, sans-serif;
    font-weight: 700;
        }
        .status100-terms-text {
             font-size: 0.875rem;
    color: #111827;
    letter-spacing: -.025em;
    font-family: Roboto, sans-serif;
    font-weight: 500;
          line-height: 1.7;
        }
        .status100-powered {
          text-align: center;
          margin-top: 16px;
          padding-top: 14px;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          font-weight: 500;
        }
        @media screen and (max-width: 900px) {
          /*
           * Mobile header: (1) title top + centered.
           * (2) Smaller logo, left.
           * (3) Order # / date / PO + badge, right-aligned.
           */
          #order-summary-print .status100-hero.status100-pad {
            padding-bottom: 20px;
            border-bottom: 1px solid #e5e7eb;
          }
          #order-summary-print .status100-padcustom {
            padding: 20px 32px 3px;
          }
          #order-summary-print .status100-padcustom > .status100-section:first-child {
            margin-top: 0;
          }
            .ordercards{
            padding-left: 0px !important;
            padding-right: 0px !important;
            }
          .status100-hero {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: auto auto auto;
            column-gap: 0;
            row-gap: 10px;
          }
          .status100-hero-title-wrap {
            grid-column: 1;
            grid-row: 1;
            justify-self: center;
            text-align: center;
            width: 100%;
            max-width: 100%;
          }
          .status100-hero .status100-brand {
            grid-column: 1;
            grid-row: 2;
            justify-self: start;
            align-self: start;
            width: auto;
            display: block;
            text-align: left;
          }
          .status100-hero .status100-brand img {
            margin-left: 0;
            margin-right: 0;
            max-width: 150px;
            max-height: 48px;
          }
          .status100-hero .status100-brand-name {
            font-size: 20px;
            line-height: 1.15;
          }
          .status100-hero .status100-header-right {
            grid-column: 1;
            grid-row: 3;
            justify-self: end;
            width: 100%;
            min-width: 0;
            max-width: 100%;
            align-items: flex-end;
            text-align: right;
          }
          .status100-title {
            width: 100%;
            text-align: center;
          }
          #order-summary-print .status100-hero .status100-title {
            font-size: 18px;
            line-height: 1.2;
          }
          .status100-title--page-center {
            position: static;
            left: auto;
            transform: none;
            width: 100%;
            white-space: normal;
          }
          .status100-meta-grid {
            margin-left: auto;
            margin-right: 0;
            width: max-content;
            max-width: 100%;
            justify-items: unset;
            grid-template-columns: max-content max-content;
            column-gap: 8px;
          }
          .status100-meta-grid .status100-meta-label {
            text-align: left;
          }
          .status100-meta-grid .status100-meta-value {
            text-align: right;
          }
          .status100-badge {
            align-self: flex-end;
          }
          .status100-section-head {
            justify-content: flex-start;
          }
          .status100-customer-grid,
          .status100-address-grid {
            grid-template-columns: 1fr;
          }
          .status100-job-details-row {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }
          .status100-customer-grid > div {
            text-align: center;
          }
          .status100-section--customer .status100-customer-grid > div {
            text-align: left;
          }
          .status100-section--delivery .status100-address-card,
          .status100-section--delivery .status100-address-label,
          .status100-section--delivery .status100-address-line {
            text-align: left;
          }
        }
        @media screen and (max-width: 640px) {
          .status100-pad,
          .status100-footer {
            padding: 20px 16px;
          }
          #order-summary-print .status100-padcustom {
            padding: 20px 16px 3px;
          }
          #order-summary-print .status100-hero .status100-title {
            font-size: 16px;
            line-height: 1.2;
          }
          #order-summary-print .status100-hero .status100-brand img {
            max-width: 120px;
            max-height: 40px;
          }
          #order-summary-print .status100-hero .status100-brand-name {
            font-size: 18px;
          }
          .invoice-footer-grid { grid-template-columns: 1fr; }
          .invoice-footer-field-address { grid-column: auto; }
          .status100-job-details-row {
            flex-direction: column;
            flex-wrap: wrap;
            gap: 14px;
          }
        }

        /* ≤1021px: card layout for line items (matches InvoiceDocument / custom.css breakpoint) */
        @media screen and (max-width: 1021px) {
          #order-summary-print .rdt-desktop,
          .order-summary-print-root .rdt-desktop {
            display: none !important;
          }
          #order-summary-print .rdt-mobile,
          .order-summary-print-root .rdt-mobile {
            display: block !important;
          }
          .status100-shell .status100-bottom.row .status100-bottom-notes-col,
          .status100-shell .status100-bottom.row .status100-bottom-totals-col {
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }
          /*
           * Notes + totals: remove Bootstrap horizontal gutter so width matches billing/shipping;
           * inner padding matches .status100-address-card.
           */
          #order-summary-print .status100-bottom .row.g-3 {
            --bs-gutter-x: 0;
          }
          #order-summary-print .status100-notes-box {
            padding: 18px 18px 18px 20px;
            min-height: 0;
            box-shadow: none;
          }
          #order-summary-print .status100-payment-card {
            padding: 18px 18px 18px 20px;
            box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
          }
        }

        #order-summary-print .status100-mobile-items,
        .order-summary-print-root .status100-mobile-items {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
        }
        #order-summary-print .status100-mobile-items .po-card,
        .order-summary-print-root .status100-mobile-items .po-card {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        #order-summary-print .status100-mobile-item-head,
        .order-summary-print-root .status100-mobile-item-head {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }
        #order-summary-print .status100-mobile-item-head-main,
        .order-summary-print-root .status100-mobile-item-head-main {
          flex: 1 1 0%;
          min-width: 0;
        }
        #order-summary-print .status100-mobile-item-fields,
        .order-summary-print-root .status100-mobile-item-fields {
          margin-top: 12px;
        }
        #order-summary-print .rdt-mobile .status100-mobile-item-head > div:first-child,
        .order-summary-print-root .rdt-mobile .status100-mobile-item-head > div:first-child {
          flex-shrink: 0;
          min-width: 80px;
        }
        #order-summary-print .rdt-mobile .status100-mobile-item-head img.status100-thumb,
        .order-summary-print-root .rdt-mobile .status100-mobile-item-head img.status100-thumb {
          max-width: none;
          width: 80px;
          height: 80px;
          object-fit: contain;
        }
        /* Mobile line cards: Details stacked; Qty / Unit / Discount / Tax / Total — label left, value right */
        #order-summary-print .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child),
        .order-summary-print-root .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
        }
        #order-summary-print .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-label,
        .order-summary-print-root .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-label {
          margin-bottom: 0;
          flex: 0 0 auto;
          min-width: 0;
        }
        #order-summary-print .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value,
        .order-summary-print-root .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value {
          flex: 1 1 auto;
          min-width: 0;
          text-align: right;
          font-weight: 500;
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }
        #order-summary-print .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value.status100-total-bold,
        .order-summary-print-root .rdt-mobile .status100-mobile-item-fields > .po-field:not(:first-child) .po-field-value.status100-total-bold {
          font-weight: 600;
        }
        /* Screen: printable root (InvoiceDocument-style wrapper) */
        .order-summary-print-inner {
          width: 100%;
          max-width: 940px;
          margin: 0 auto;
        }

        /*
         * Paged margin + page numbers (same idea as InvoiceDocument SCREEN_STYLES).
         * Chrome may ignore @bottom-right; margin still controls printable area.
         * To remove URL/title/date from paper: in the print dialog, turn OFF "Headers and footers".
         */
        @page {
          size: A4 portrait;
          margin: 10mm 10mm 16mm 10mm;
          @bottom-right {
            content: counter(page) " of " counter(pages);
            font-size: 11px;
            color: #64748b;
          }
        }

        /* ========== Print: Invoice-style — only #order-summary-print, less blank gaps ========== */
        @media print {
          html,
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            background: #fff !important;
          }
          /* Print only the order summary (hide app chrome), same pattern as #invoice-pdf */
          body * {
            visibility: hidden !important;
          }
          #order-summary-print,
          #order-summary-print * {
            visibility: visible !important;
          }
          #order-summary-print .rdt-mobile {
            display: none !important;
          }
          #order-summary-print .rdt-desktop {
            display: block !important;
          }
          .no-print,
          .d-print-none {
            display: none !important;
          }
          .order-summary-print-root {
            padding: 0 !important;
            margin: 0 !important;
            background: #fff !important;
            max-width: none !important;
          }
          #order-summary-print {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
          }
          #order-summary-print .status100-shell {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: 1px solid #d4dae4 !important;
            border-radius: 12px !important;
            overflow: visible !important;
            background: #fff !important;
          }
          // .status100-pad {
          //   padding: 22px 26px !important;
          // }
          .status100-footer {
            padding: 20px 26px 22px !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Override responsive breakpoints: always use multi-column desktop layout when printing */
          #order-summary-print .status100-hero {
            display: grid !important;
            grid-template-columns: auto 1fr auto !important;
            grid-template-rows: auto auto !important;
            column-gap: 20px !important;
            row-gap: 10px !important;
            align-items: start !important;
          }
          #order-summary-print .status100-hero-title-wrap {
            grid-column: 1 / -1 !important;
            justify-self: center !important;
            text-align: center !important;
            width: 100% !important;
          }
          #order-summary-print .status100-hero .status100-brand {
            grid-column: 1 !important;
            grid-row: 2 !important;
            justify-self: start !important;
            align-self: center !important;
          }
          #order-summary-print .status100-hero .status100-header-right {
            grid-column: 3 !important;
            grid-row: 2 !important;
            justify-self: end !important;
          }
          .status100-header-right {
            width: auto !important;
            min-width: 220px !important;
            max-width: 50% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-end !important;
            align-self: flex-start !important;
          }
          .status100-meta-grid {
            width: max-content !important;
            max-width: 100% !important;
            margin-left: auto !important;
            margin-right: 0 !important;
            justify-items: unset !important;
          }
          #order-summary-print .status100-brand {
            display: block !important;
            text-align: left !important;
            align-items: unset !important;
          }
          #order-summary-print .status100-brand img {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          #order-summary-print .order-summary-title-row {
            width: 100% !important;
            display: block !important;
            text-align: center !important;
          }
          #order-summary-print .order-summary-title-row .status100-title {
            text-align: center !important;
            width: 100% !important;
          }
          #order-summary-print .status100-title-subtitle {
            display: block !important;
            margin-top: 6px !important;
            font-size: 16px !important;
            font-weight: 700 !important;
            letter-spacing: 0.02em !important;
            // color: #6b7280 !important;
            text-align: center !important;
            white-space: normal !important;
          }
          #order-summary-print .order-summary-title-word {
            display: inline-block !important;
          }
          #order-summary-print .order-summary-title-word:not(.is-last) {
            margin-right: 0.34em !important;
          }
          #order-summary-print .status100-title--page-center {
            position: absolute !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: max-content !important;
            text-align: center !important;
            white-space: nowrap !important;
          }
          #order-summary-print .status100-meta-grid .status100-meta-label {
            text-align: left !important;
            justify-self: start !important;
          }
          #order-summary-print .status100-meta-grid .status100-meta-value {
            text-align: right !important;
            justify-self: end !important;
          }
          #order-summary-print .status100-section-head {
            justify-content: flex-start !important;
          }
          #order-summary-print .status100-customer-grid > div {
            text-align: left !important;
          }
          #order-summary-print .status100-address-card,
          #order-summary-print .status100-address-label,
          #order-summary-print .status100-address-line {
            text-align: left !important;
          }
          #order-summary-print .status100-job-details-row {
            flex-direction: row !important;
            align-items: flex-start !important;
            text-align: left !important;
          }
          #order-summary-print .status100-job-details-col {
            text-align: left !important;
          }
          .status100-customer-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 14px 18px !important;
          }
          .status100-address-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 14px !important;
          }
          .status100-table-wrap {
            overflow: visible !important;
          }
          .status100-table {
            min-width: 0 !important;
            width: 100% !important;
            border-collapse: collapse !important;
            table-layout: fixed !important;
          }
          #order-summary-print .status100-table th,
          #order-summary-print .status100-table td {
            padding: 12px 10px !important;
            vertical-align: top !important;
            text-align: left !important;
          }
          /* Print: reset extra image/product gap so headers and values align cleanly */
          #order-summary-print .status100-table th:nth-child(1),
          #order-summary-print .status100-table td:nth-child(1) {
            padding-right: 50px !important;
            width: 9% !important;
          }
          #order-summary-print .status100-table th:nth-child(2),
          #order-summary-print .status100-table td:nth-child(2) {
            padding-left: 24px !important;
            width: 24% !important;
          }
          #order-summary-print .status100-table td:nth-child(2) .status100-product-name,
          #order-summary-print .status100-table td:nth-child(2) .status100-product-sku {
            margin-left: 4px !important;
          }
          #order-summary-print .status100-table th:nth-child(3),
          #order-summary-print .status100-table td:nth-child(3) {
            width: 24% !important;
          }
          #order-summary-print .status100-table th:nth-child(4),
          #order-summary-print .status100-table td:nth-child(4) {
            width: 8% !important;
            text-align: center !important;
          }
          #order-summary-print .invoice-pdf-col-image {
            width: 10% !important;
            text-align: left !important;
            padding-right: 10px !important;
          }
          #order-summary-print .invoice-pdf-col-product {
            width: 18% !important;
            text-align: left !important;
            padding-left: 14px !important;
          }
          #order-summary-print .invoice-pdf-col-details {
            width: 14% !important;
            text-align: left !important;
          }
          #order-summary-print .invoice-pdf-col-qty {
            width: 6% !important;
            text-align: center !important;
          }
          #order-summary-print .invoice-pdf-col-unit {
            width: 9% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          #order-summary-print .invoice-pdf-col-changed {
            width: 9% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          #order-summary-print .invoice-pdf-col-discount {
            width: 9% !important;
            text-align: center !important;
          }
          #order-summary-print .invoice-pdf-col-tax {
            width: 8% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          #order-summary-print .invoice-pdf-col-total {
            width: 10% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          .status100-table thead tr {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Keep each row/card together; move whole block to next page if needed */
          .status100-table {
            break-inside: auto !important;
            page-break-inside: auto !important;
          }
          .status100-table tbody tr,
          .status100-table tbody td,
          .status100-section,
          .status100-section-head,
          .status100-card {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .status100-table thead {
            display: table-header-group !important;
          }
          .status100-table tfoot {
            display: table-footer-group !important;
          }
          /* Only keep small blocks from splitting awkwardly */
          .status100-address-card,
          .status100-customer-box .status100-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .status100-notes-box {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .status100-bottom {
            width: 100% !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          /*
           * Bootstrap md columns: print viewport is often narrow — force desktop two-column layout.
           * No-payment branch uses class "status100-bottom row" on ONE element (no nested .row), so
           * ".status100-bottom .row" never matched; include .status100-bottom.row explicitly.
           */
          #order-summary-print .status100-bottom.row,
          #order-summary-print .status100-bottom .row {
            display: flex !important;
            flex-wrap: wrap !important;
            width: 100% !important;
          }
          #order-summary-print .status100-bottom.row > .col-12.col-md-6,
          #order-summary-print .status100-bottom .row .col-12.col-md-6 {
            flex: 0 0 50% !important;
            max-width: 50% !important;
            width: 50% !important;
          }
          .status100-bottom .status100-payment-card {
            max-width: 100% !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .status100-grand {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .status100-badge {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            align-self: flex-end !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            white-space: nowrap !important;
            max-width: max-content !important;
            letter-spacing: normal !important;
            line-height: 1.25 !important;
          }
          .status100-pay-btn,
          .status100-pay-note {
            display: none !important;
          }
          img,
          .status100-thumb {
            max-width: 100% !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .status100-email-link,
          .status100-footer-link {
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            max-width: 100% !important;
          }
          .status100-email-cell {
            min-width: 0 !important;
            max-width: 100% !important;
          }
          /* Hide placeholder footer div if added; page numbers use @page when supported */
          .print-page-footer {
            display: none !important;
          }
        }
      `}</style>

        <div
          id="order-summary-print"
          className="order-summary-print-inner pdf-export"
        >
          <div className="status100-shell">
            <div className="status100-hero status100-pad mt-3">
              <div className="status100-hero-title-wrap order-summary-title-row">
                <div className="status100-title">
                  {(isRemovalPublicSummary
                    ? "Removal Job Summary"
                    : isInstallationPublicSummary
                      ? "Installation Job Summary"
                      : "Order Summary"
                  )
                    .split(" ")
                    .map((word, idx, arr) => (
                      <span
                        key={`${word}-${idx}`}
                        className={`order-summary-title-word${idx === arr.length - 1 ? " is-last" : ""}`}
                      >
                        {word}
                        {idx < arr.length - 1 ? "\u00A0" : ""}
                      </span>
                    ))}
                  <div className="status100-title-subtitle">THIS IS NOT AN INVOICE — Summary Only</div>
                </div>
              </div>
              <div className="status100-brand">
                {resolvedCompanyLogo ? (
                  <img
                    src={resolvedCompanyLogo}
                    data-pdf-file-id={logoFileIdForPdf || logoFileIds[0] || undefined}
                    alt={displayCompanyName}
                    loading="eager"
                    decoding="sync"
                  />
                ) : (
                  <div className="status100-brand-name">{displayCompanyName}</div>
                )}
              </div>

              <div className="status100-header-right">
                <div className="status100-meta-grid">
                  <span className="status100-meta-label">Job No</span>
                  <span className="status100-meta-value">#{displayQuoteNumber ? displayQuoteNumber : "—"}</span>

                  <span className="status100-meta-label">Date</span>
                  <span className="status100-meta-value"># {orderDateMdY}</span>

                  <span className="status100-meta-label">PO No</span>
                  <span className="status100-meta-value"># {displayPONo}</span>

                  {displayProjectNo ? (
                    <>
                      <span className="status100-meta-label">Project No</span>
                      <span className="status100-meta-value"># {displayProjectNo}</span>
                    </>
                  ) : null}
                </div>

                <div className="status100-badge">{displayPaymentStatus}</div>
              </div>
            </div>

            <div className="status100-padcustom">
              <div className="status100-section status100-section--customer">
                <div className="status100-section-head">
                  <div style={sectionIconBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div style={sectionTitle}>Customer Information</div>
                </div>

                <div className="status100-card status100-customer-box">
                  <div className="status100-customer-grid">
                    <div>
                      <div style={fieldLabel}>Brokerage</div>
                      <div style={fieldValue}>{show(displayBrokerage)}</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Office</div>
                      <div style={fieldValue}>{show(displayOffice)}</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>{salesAgentOrTeamMemberLabel}</div>
                      <div style={fieldValue}>{show(displayAgentOrCustomerName)}</div>
                    </div>
                    <div className="status100-email-cell">
                      <div style={fieldLabel}>Email</div>
                      <div style={fieldValue}>
                        {displayEmail ? (
                          <a className="status100-email-link" href={`mailto:${displayEmail}`}>
                            {displayEmail}
                          </a>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Phone</div>
                      <div style={fieldValue}>{show(displayPhone)}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="status100-section status100-section--delivery">
                <div className="status100-section-head">
                  <div style={sectionIconBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div style={sectionTitle}>Delivery Details</div>
                </div>

                <div className="status100-address-grid">
                  <div className="status100-card status100-address-card">
                    <div className="status100-address-label">Billing Address</div>
                    {billingLines.map((line, index) => (
                      <div
                        key={`billing-${index}`}
                        className={`status100-address-line ${index === 0 ? "is-name" : ""}`}
                      >
                        {line}
                      </div>
                    ))}
                  </div>

                  <div className="status100-card status100-address-card">
                    <div className="status100-address-label">
                      {deliveryAddressLabelForOrder(summaryOrderType, publicOrder)}
                    </div>
                    {shippingLines.map((line, index) => (
                      <div
                        key={`shipping-${index}`}
                        className={`status100-address-line ${index === 0 ? "is-name" : ""}`}
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {isJobOrderSummary ? (
                <div className="status100-section">
                  <div className="status100-section-head">
                    <div style={sectionIconBox}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    </div>
                    <div style={sectionTitle}>
                      {isRemovalPublicSummary ? "Removal Details" : "Installation Details"}
                    </div>
                  </div>
                  <div className="status100-card status100-job-details-card">
                    <div className="status100-job-details-row">
                      <div className="status100-job-details-col">
                        <div style={fieldLabel}>Scheduled Date</div>
                        <div style={fieldValue}>{show(jobScheduledDateLabel)}</div>
                      </div>
                      <div className="status100-job-details-col">
                        <div style={fieldLabel}>Assigned To</div>
                        <div style={fieldValue}>{show(jobAssignedToLabel)}</div>
                      </div>
                      <div className="status100-job-details-col">
                        <div style={fieldLabel}>Priority</div>
                        <div style={fieldValue}>
                          <span className="status100-job-priority-pill">{show(jobPriorityLabel)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="status100-section">
                <div className="status100-section-head">
                  <div style={sectionIconBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <path d="M3 10h18" />
                    </svg>
                  </div>
                  <div style={sectionTitle}>Line Items</div>
                </div>

                <div className="rdt-desktop">
                  <div className="status100-table-wrap">
                    <table className="status100-table invoice-pdf-items-table">
                      <thead>
                        <tr>
                          <th className="invoice-pdf-col-image">Image</th>
                          <th className="invoice-pdf-col-product">Product</th>
                          <th className="invoice-pdf-col-details">Details</th>
                          <th className="status100-qty invoice-pdf-col-qty">Qty</th>
                          {!isRemovalPublicSummary ? (
                            <>
                              <th className="status100-right invoice-pdf-col-changed" title="Unit Price">
                                Unit<br />Price
                              </th>
                              <th className="status100-center invoice-pdf-col-discount">Discount</th>
                              {/* <th className="status100-right invoice-pdf-col-tax">Tax</th> */}
                              <th className="status100-right invoice-pdf-col-total">Total</th>
                            </>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {displayItems.length ? (
                          displayItems.map((item, index) => {
                            return (
                              <tr className="invoice-item-row" key={item.id ?? index}>
                                <td className="invoice-pdf-col-image">
                                  <div className="invoice-pdf-image-cell-wrap">
                                    <OrderSummaryLineItemThumb
                                      imageUrl={item.imageUrl}
                                      pdfFileId={item.pdfFileId}
                                      imageAlt={item.imageAlt}
                                      productName={item.productName}
                                    />
                                  </div>
                                </td>
                                <td className="invoice-pdf-col-product">
                                  <div className="status100-product-name">{show(item.productName)}</div>
                                  <div className="status100-product-sku">SKU: {formatSkuDisplay(item.sku)}</div>
                                  <OrderSummaryLineItemCustomizationsBlock
                                    customizations={item.customizations}
                                    customizationImgMap={customizationImgMap}
                                  />
                                  {!isRemovalPublicSummary ? (
                                    <OrderSummaryLineItemCustomInformationBlock
                                      inventorySource={item.inventorySource}
                                      customInformation={item.customInformation}
                                    />
                                  ) : null}
                                </td>
                                <td className="invoice-pdf-col-details">
                                  <OrderSummaryLineItemAttrsBlock details={item.details} />
                                </td>
                                <td className="status100-qty invoice-pdf-col-qty">{show(item.quantity ?? 0)}</td>
                                {!isRemovalPublicSummary ? (
                                  <>                                    <td className="status100-right invoice-pdf-col-changed">{money(item.unitRetailPrice)}</td>
                                    <td className="status100-center invoice-pdf-col-discount">
                                      <DiscountAmountDisplay amount={item.lineDiscount} />
                                    </td>
                                    {/* <td className="status100-right invoice-pdf-col-tax">{money(item.tax)}</td> */}
                                    <td className="status100-right status100-total-bold invoice-pdf-col-total">{money(item.total)}</td>
                                  </>
                                ) : null}

                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={8} style={{ textAlign: "center", color: "#6b7280" }}>
                              No items available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rdt-mobile">
                  <div className="status100-mobile-items">
                    {displayItems.length ? (
                      displayItems.map((item, index) => {
                        return (
                          <div key={item.id ?? index} className="po-card">
                            <div className="status100-mobile-item-head">
                              <OrderSummaryLineItemThumb
                                imageUrl={item.imageUrl}
                                pdfFileId={item.pdfFileId}
                                imageAlt={item.imageAlt}
                                productName={item.productName}
                              />
                              <div className="status100-mobile-item-head-main">
                                <div className="status100-product-name">{show(item.productName)}</div>
                                <div className="status100-product-sku">SKU: {formatSkuDisplay(item.sku)}</div>
                                <OrderSummaryLineItemCustomizationsBlock
                                  customizations={item.customizations}
                                  customizationImgMap={customizationImgMap}
                                />
                                {!isRemovalPublicSummary ? (
                                  <OrderSummaryLineItemCustomInformationBlock
                                    inventorySource={item.inventorySource}
                                    customInformation={item.customInformation}
                                  />
                                ) : null}
                              </div>
                            </div>
                            <div className="status100-mobile-item-fields">
                              <div className="po-field">
                                <div className="po-field-label">Details</div>
                                <div className="po-field-value">
                                  <OrderSummaryLineItemAttrsBlock details={item.details} />
                                </div>
                              </div>
                              <div className="po-field">
                                <div className="po-field-label">Qty</div>
                                <div className="po-field-value">{show(item.quantity ?? 0)}</div>
                              </div>
                              {!isRemovalPublicSummary ? (
                                <>
                                  <div className="po-field">
                                    <div className="po-field-label">Unit Price</div>
                                    <div className="po-field-value">{money(item.unitRetailPrice)}</div>
                                  </div>
                                  <div className="po-field">
                                    <div className="po-field-label">Discount</div>
                                    <div className="po-field-value">
                                      <DiscountAmountDisplay amount={item.lineDiscount} />
                                    </div>
                                  </div>
                                  <div className="po-field">
                                    <div className="po-field-label">Tax</div>
                                    <div className="po-field-value">{money(item.tax)}</div>
                                  </div>
                                  <div className="po-field">
                                    <div className="po-field-label">Total</div>
                                    <div className="po-field-value status100-total-bold">{money(item.total)}</div>
                                  </div>
                                </>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="po-card" style={{ textAlign: "center", color: "#6b7280" }}>
                        No items available.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "3px", marginTop: "25px" }}>
                {!isRemovalPublicSummary && orderLinkedProjectId && hasApprovedMessage ? (
                  <>
                    {approvedProofCards.map((card) => (
                      <CompactApprovedProofCard
                        key={`approved-proof-v-${card.version}`}
                        version={card.version}
                        files={card.files}
                        approvedBy={card.approvedBy}
                        approvedAt={card.approvedAt !== "—" ? card.approvedAt : ""}
                        footerBy={card.assignedToName}
                        shippingName={card.approvedShippingName}
                        shippingDesc={card.approvedShippingDesc}
                        shippingAmountLabel={card.approvedShippingAmount}
                        showOrderSummary={Boolean(card.orderSummary)}
                        orderSummary={card.orderSummary}
                      />
                    ))}
                  </>

                ) : null}
              </div>

              {hasPaymentDetails ? (
                <div className="status100-bottom status100-bottom-has-payment">
                  <div className="row g-3 mx-0 w-100 align-items-start">
                    <div className="col-12 status100-bottom-notes-col">{orderNotesSection}</div>
                    <div className="col-12">
                      <div className="row g-3 mx-0 w-100 align-items-start">
                        <div className="col-12 col-md-6">{paymentDetailsCardInner}</div>
                        <div className="col-12 col-md-6 status100-bottom-totals-col d-flex flex-column gap-3">
                          {orderTotalPaymentCard}
                          {payActionsBlock}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="status100-bottom row g-3 mx-0 w-100 align-items-start ordercards">
                  <div className="col-12 col-md-6 status100-bottom-notes-col">{orderNotesSection}</div>
                  <div className="col-12 col-md-6 status100-bottom-totals-col d-flex flex-column gap-3">
                    {orderTotalPaymentCard}
                    {payActionsBlock}
                  </div>
                </div>
              )}
            </div>
            {/* 
          <button type="button" className="status100-pay-btn" onClick={openPaymentModal}>
                    {payText}
                  </button> */}

            <div className="status100-footer">
              <div className="invoice-footer-grid">
                <div className="invoice-footer-field">
                  <span className="invoice-footer-k">Company/Vendor Name</span>
                  <span className="invoice-footer-v">{show(displayCompanyName)}</span>
                </div>
                <div className="invoice-footer-field">
                  <span className="invoice-footer-k">Contact</span>
                  <span className="invoice-footer-v">{show(displayFooterPhone)}</span>
                </div>
                <div className="invoice-footer-field">
                  <span className="invoice-footer-k">Website</span>
                  <span className="invoice-footer-v invoice-footer-website">
                    {displayFooterWebsite ? (
                      <a
                        className="status100-footer-link"
                        href={
                          /^https?:\/\//i.test(displayFooterWebsite)
                            ? displayFooterWebsite
                            : `https://${displayFooterWebsite}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {displayFooterWebsite}
                      </a>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
                <div className="invoice-footer-field">
                  <span className="invoice-footer-k">Email</span>
                  <span className="invoice-footer-v">
                    {displayFooterEmail ? (
                      <a className="status100-footer-link" href={`mailto:${displayFooterEmail}`}>
                        {displayFooterEmail}
                      </a>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
                <div className="invoice-footer-field invoice-footer-field-address">
                  <span className="invoice-footer-k">Address</span>
                  <span className="invoice-footer-v">{show(displayFooterAddress)}</span>
                </div>
              </div>

              <div className="status100-terms">
                <div className="status100-terms-label">Terms & Conditions</div>
                <div className="status100-terms-text">{displayTermsText || "—"}</div>
              </div>

              <div className="status100-powered">
                {poweredByUrl ? (
                  <>
                    <span className="status100-powered-lead">{poweredByPrefix}</span>
                    <a
                      className="status100-powered-brand"
                      href={poweredByUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {poweredByBrandText}
                    </a>
                  </>
                ) : (
                  poweredByText
                )}
              </div>
            </div>
          </div>
        </div>
        {showPayModal ? (
          <div
            className="no-print"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 24, 39, 0.6)",
              zIndex: 3000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              setShowPayModal(false);
              setPaymentInitError(null);
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 420,
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
                padding: 18,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h5 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Enter Card Details</h5>
                <button
                  type="button"
                  onClick={() => {
                    setShowPayModal(false);
                    setPaymentInitError(null);
                  }}
                  style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1 }}
                >
                  x
                </button>
              </div>
              {loadingPaymentUi ? (
                <div style={{ padding: "24px 0", textAlign: "center", color: "#6b7280" }}>Loading payment form...</div>
              ) : stripePromise && clientSecret ? (
                <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                  {isRemovalPublicSummary ? (
                    <RemovalPublicPayments
                      supplierId={stripeGatewayUserId}
                      emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                      totalAmount={Number(displayPaymentRemaining || 0)}
                      dueAmount={0}
                      orderId={queryOrderId}
                      projectId={effectiveProjectId}
                      publicToken={effectiveAccessToken}
                      selectedPaymentMethod="card"
                      createdBy={paymentCreatedBy}
                      paidBy={paymentPaidBy}
                      onPaymentSuccess={async () => {
                        setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                        setShowPayModal(false);
                        setPaymentInitError(null);
                        setShowPaymentSuccessModal(true);
                        try {
                          if (!queryOrderId || !paymentTriggerToken) {
                            console.warn("[Order_summary] Skipping removal payment email: missing orderId/token");
                            return;
                          }
                          await sendRemovalOrderTriggerEmail({
                            triggerName: "Public Signage Payment applied for removal Job",
                            removalOrderId: queryOrderId,
                            token: paymentTriggerToken,
                            includeOrderPdfAttachment: true,
                          });
                        } catch (emailErr) {
                          console.warn("[Order_summary] Failed to send payment trigger email:", emailErr);
                        }
                        try {
                          if (!queryOrderId || !paymentTriggerToken) return;
                          const normalized = await fetchNormalizedPublicOrderForSummary(
                            true,
                            false,
                            effectiveProjectId || "",
                            queryOrderId,
                            paymentTriggerToken
                          );
                          setPublicOrder(normalized);
                        } catch (e) {
                          console.warn("[Order_summary] Removal public order refetch:", e);
                        }
                      }}
                    />
                  ) : isInstallationPublicSummary ? (
                    <InstallationPublicPayments
                      supplierId={stripeGatewayUserId}
                      emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                      totalAmount={Number(displayPaymentRemaining || 0)}
                      dueAmount={0}
                      orderId={queryOrderId}
                      projectId={effectiveProjectId}
                      publicToken={effectiveAccessToken}
                      selectedPaymentMethod="card"
                      createdBy={paymentCreatedBy}
                      paidBy={paymentPaidBy}
                      onPaymentSuccess={async () => {
                        setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                        setShowPayModal(false);
                        setPaymentInitError(null);
                        setShowPaymentSuccessModal(true);
                        try {
                          if (!queryOrderId || !paymentTriggerToken) {
                            console.warn("[Order_summary] Skipping installation payment email: missing orderId/token");
                            return;
                          }
                          // await sendInstallationOrderTriggerEmail({
                          //   triggerName: "Signage Payment applied for Installation Job",
                          //   installationOrderId: queryOrderId,
                          //   token: paymentTriggerToken,
                          //   includeOrderPdfAttachment: true,
                          // });
                        } catch (emailErr) {
                          console.warn("[Order_summary] Failed to send payment trigger email:", emailErr);
                        }
                        try {
                          if (!queryOrderId || !paymentTriggerToken) return;
                          const normalized = await fetchNormalizedPublicOrderForSummary(
                            false,
                            true,
                            effectiveProjectId || "",
                            queryOrderId,
                            paymentTriggerToken
                          );
                          setPublicOrder(normalized);
                        } catch (e) {
                          console.warn("[Order_summary] Installation public order refetch:", e);
                        }
                      }}
                    />
                  ) : (
                    <PublicPayments
                      supplierId={stripeGatewayUserId}
                      emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                      totalAmount={Number(displayPaymentRemaining || 0)}
                      dueAmount={0}
                      orderId={queryOrderId}
                      projectId={effectiveProjectId}
                      publicToken={effectiveAccessToken}
                      selectedPaymentMethod="card"
                      createdBy={paymentCreatedBy}
                      paidBy={paymentPaidBy}
                      onPaymentSuccess={async () => {
                        setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                        setShowPayModal(false);
                        setPaymentInitError(null);
                        setShowPaymentSuccessModal(true);
                        try {
                          if (!queryOrderId || !paymentTriggerToken) {
                            console.warn("[Order_summary] Skipping sales payment email: missing orderId/token");
                            return;
                          }
                          await sendSalesOrderTriggerEmail({
                            triggerName: "Public Signage Payment applied for order",
                            orderId: queryOrderId,
                            token: paymentTriggerToken,
                            paymentMethodName: "card",
                            includeOrderPdfAttachment: true,
                          });
                        } catch (emailErr) {
                          console.warn("[Order_summary] Failed to send payment trigger email:", emailErr);
                        }
                        try {
                          if (!queryOrderId || !paymentTriggerToken) return;
                          const normalized = await fetchNormalizedPublicOrderForSummary(
                            false,
                            false,
                            effectiveProjectId || "",
                            queryOrderId,
                            paymentTriggerToken
                          );
                          setPublicOrder(normalized);
                        } catch (e) {
                          console.warn("[Order_summary] Sales public order refetch:", e);
                        }
                      }}
                    />
                  )}
                </Elements>
              ) : (
                <div style={{ padding: "24px 0", textAlign: "center", color: "#dc2626" }}>
                  {paymentInitError ||
                    "Unable to initialize Stripe payment. Ensure client_secret is in the link, the order has loaded, and Stripe gateway is configured for this supplier."}
                </div>
              )}
            </div>
          </div>
        ) : null}
        {showPaymentSuccessModal ? (
          <div
            className="no-print"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 24, 39, 0.55)",
              zIndex: 3200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              setShowPaymentSuccessModal(false);
              setPaymentSuccessChargedAmount(null);
            }}
            role="presentation"
          >
            <div
              style={{
                width: "100%",
                maxWidth: 400,
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
                padding: "28px 24px 22px",
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-labelledby="payment-success-title"
              aria-modal="true"
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  margin: "0 auto 16px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="#fff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 id="payment-success-title" style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: "#111827" }}>
                Payment successful
              </h2>
              <p style={{ margin: "0 0 8px", fontSize: 15, color: "#4b5563", lineHeight: 1.5 }}>
                Thank you. Your payment for order {show(displayQuoteNumber)} was received.
              </p>
              <p style={{ margin: "0 0 22px", fontSize: 16, fontWeight: 600, color: "#3b6fe8" }}>
                {money(paymentSuccessChargedAmount ?? displayPaymentRemaining)} paid
              </p>
              <button
                type="button"
                className="export-button"
                onClick={() => {
                  setShowPaymentSuccessModal(false);
                  setPaymentSuccessChargedAmount(null);
                  window.location.reload();
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#fff",
                  background: "#00a63e !important",
                  border: "none",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  });

OrderSummaryStatus100Template.displayName = "OrderSummaryStatus100Template";

export default OrderSummaryStatus100Template;
